/**
 * Device Import Wizard
 *
 * 3-step Dialog matching UserImportWizard shell:
 *   Step 1 — Upload CSV (with blank template + example row downloads)
 *   Step 2 — Review, edit, validate (per-row status, inline edits, duplicate detection)
 *   Step 3 — Run & Result (progress + Created/Skipped/Failed cards + undo pointer note)
 *
 * Duplicate mode is fixed to "Skip existing" (per user requirement).
 * Undo lives on the Device Management page: a run manifest is recorded via
 * POST /admin/devices/import-runs and can be undone there for 30 minutes.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { newLandscapeDoc, addBrandHeader, addFooters, loadLogoPng, exportTableHtml, SLATE_900, SLATE_600, SLATE_500, BLUE, BORDER } from '../../utils/tripExport';
import * as XLSX from 'xlsx';
import {
  FileText,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Download,
  HardDrive,
  Undo2,
  Users,
  Copy,
  X,
  Search,
  Shield,
  Printer,
  Upload,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';

/* ============================================================
 * Static enums & aliases
 * ============================================================ */

const PROTOCOL_OPTIONS = ['Teltonika', 'Meitrack', 'Digital Matter', 'Jimi Concox', 'CalAmp', 'BLE Beacon'];

const DEVICE_TYPE_OPTIONS = [
  { value: 'gps_tracker', label: 'GPS Tracker' },
  { value: 'obd_tracker', label: 'OBD Tracker' },
  { value: 'personal_tracker', label: 'Personal Tracker' },
  { value: 'asset_tracker', label: 'Asset Tracker' },
  { value: 'dashcam', label: 'Dashcam' },
  { value: 'beacon', label: 'Beacon' },
];

const ASSET_TYPE_OPTIONS = [
  'car', 'suv', 'van', 'ute', 'truck', 'bus', 'motorcycle', 'trailer',
  'machine', 'excavator', 'ebike', 'scooter', 'caravan', 'boat', 'jetski',
  'container', 'personnel', 'device', 'building',
  'people', 'tool', 'equipment', 'pallet', 'cargo', 'temperature_zone', 'door', 'vehicle', 'other',
];

const BEACON_TYPE_OPTIONS = ['ibeacon', 'eddystone', 'vendor_specific'];

// Human-readable label for an asset type. "SUV", "VIN" style acronyms stay
// uppercase, everything else Title Case.
const ASSET_TYPE_LABELS = {
  car: 'Car', suv: 'SUV', van: 'Van', ute: 'Ute', truck: 'Truck', bus: 'Bus',
  motorcycle: 'Motorcycle', trailer: 'Trailer', machine: 'Machine',
  excavator: 'Excavator', ebike: 'E-Bike', scooter: 'Scooter',
  caravan: 'Caravan', boat: 'Boat', jetski: 'Jetski',
  container: 'Container', personnel: 'Personnel', device: 'Device',
  building: 'Building / Property',
  people: 'People', tool: 'Tool', equipment: 'Equipment', pallet: 'Pallet',
  cargo: 'Cargo', temperature_zone: 'Temperature Zone', door: 'Door',
  vehicle: 'Vehicle', other: 'Other',
};
const labelAssetType = (t) => ASSET_TYPE_LABELS[t] || (t ? String(t).split(/[_\s]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '');

// Canonical field keys we understand from the CSV
const IMPORT_FIELD_KEYS = new Set([
  'name', 'imei', 'serial_number', 'mac_address', 'protocol', 'model', 'device_type',
  'sim_iccid', 'sim_number', 'sim_provider',
  'asset_name', 'asset_type', 'asset_make', 'asset_model', 'asset_year',
  'registration', 'vin_number', 'notes',
  'allocated_tenant_ids',
  'management_admin_ids',
  // Beacon-only
  'beacon_type', 'beacon_uuid', 'beacon_major', 'beacon_minor',
  'beacon_namespace', 'beacon_instance_id', 'beacon_eid',
]);

// Header aliases → canonical field. Accept both the export headers and common variants.
const HEADER_ALIASES = {
  'name': 'name',
  'imei': 'imei',
  'serial_number': 'serial_number',
  'serial': 'serial_number',
  'mac_address': 'mac_address',
  'mac': 'mac_address',
  'protocol': 'protocol',
  'model': 'model',
  'device_type': 'device_type',
  'type': 'device_type',
  'allocated_users': 'allocated_tenant_ids',
  'allocated_tenant_ids': 'allocated_tenant_ids',
  'managed_by': 'management_admin_ids',
  'management_admins': 'management_admin_ids',
  'management_admin_ids': 'management_admin_ids',
  'asset_name': 'asset_name',
  'asset_type': 'asset_type',
  'sim_provider': 'sim_provider',
  'sim_iccid': 'sim_iccid',
  'iccid': 'sim_iccid',
  'sim_mobile': 'sim_number',
  'sim_number': 'sim_number',
  'registration': 'registration',
  'registration_number': 'registration',
  'make': 'asset_make',
  'asset_make': 'asset_make',
  'asset_model': 'asset_model',
  'year': 'asset_year',
  'asset_year': 'asset_year',
  'vin': 'vin_number',
  'vin_number': 'vin_number',
  'notes': 'notes',
  // Beacon identifiers
  'beacon_type': 'beacon_type',
  'beacon_uuid': 'beacon_uuid',
  'beacon_major': 'beacon_major',
  'beacon_minor': 'beacon_minor',
  'beacon_namespace': 'beacon_namespace',
  'beacon_instance_id': 'beacon_instance_id',
  'beacon_eid': 'beacon_eid',
};

/* ============================================================
 * CSV helpers
 * ============================================================ */

const normalizeHeader = (h) =>
  (h || '')
    .toLowerCase()
    .replace(/\(km\)|\(read only\)/gi, '')
    .trim()
    .replace(/[\s-]+/g, '_');

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (ch === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current);
  // Strip Excel-safe "=..." wrappers and surrounding quotes
  return values.map(v => v.trim().replace(/^=/, '').replace(/^"|"$/g, '').replace(/""/g, '"'));
};

const normalizeMacToColon = (raw) => {
  if (!raw) return raw;
  const clean = String(raw).replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  if (clean.length !== 12) return String(raw).trim();
  return clean.match(/.{2}/g).join(':');
};

/* ============================================================
 * Template downloads — separate schemas for GPS Devices vs Beacons
 * ============================================================ */

const DEVICE_TEMPLATE_HEADERS = [
  // Shared block (positions 1-8) — identical to Export for round-trip compatibility
  'name', 'serial_number', 'mac_address', 'protocol', 'model', 'device_type', 'allocated_users', 'managed_by',
  // GPS-specific
  'imei', 'sim_provider', 'sim_iccid', 'sim_number',
  // Asset details
  'asset_name', 'asset_type', 'asset_make', 'asset_model', 'asset_year',
  'registration', 'vin_number',
  'notes',
];

const BEACON_TEMPLATE_HEADERS = [
  // Shared block (positions 1-8) — identical to Export for round-trip compatibility
  'name', 'serial_number', 'mac_address', 'protocol', 'model', 'device_type', 'allocated_users', 'managed_by',
  // Beacon-specific
  'beacon_type', 'beacon_uuid', 'beacon_major', 'beacon_minor',
  'beacon_namespace', 'beacon_instance_id', 'beacon_eid',
  // Asset details
  'asset_name', 'asset_type',
  'notes',
];

const DEVICE_EXAMPLE_ROW = {
  name: 'Truck 001',
  imei: '350612073123456',
  serial_number: 'SN-ABC-001',
  mac_address: '',
  protocol: 'Teltonika',
  model: 'FMB920',
  device_type: 'gps_tracker',
  sim_provider: 'Telstra',
  sim_iccid: '8961030012345678901',
  sim_number: '+61400000001',
  asset_name: 'Delivery Truck 1',
  asset_type: 'truck',
  asset_make: 'Isuzu',
  asset_model: 'NPR',
  asset_year: '2022',
  registration: 'ABC-123',
  vin_number: '1HGCM82633A004352',
  allocated_users: 'admin@example.com; driver1@example.com',
  managed_by: 'admin@example.com',
  notes: 'Example GPS device — delete before importing',
};

const BEACON_EXAMPLE_ROW = {
  name: 'Beacon Warehouse Gate',
  protocol: 'BLE Beacon',
  model: 'Kontakt Smart Beacon',
  device_type: 'beacon',
  asset_name: 'Warehouse Gate',
  asset_type: 'door',
  allocated_users: '',
  managed_by: '',
  beacon_type: 'ibeacon',
  beacon_uuid: 'F7826DA6-4FA2-4E98-8024-BC5B71E0893E',
  beacon_major: '100',
  beacon_minor: '1',
  beacon_namespace: '',
  beacon_instance_id: '',
  beacon_eid: '',
  mac_address: '',
  serial_number: '',
  notes: 'Example beacon — delete before importing',
};

const csvEscape = (v) => {
  const s = String(v ?? '');
  const needsQuote = /[",\n]/.test(s);
  const esc = s.replace(/"/g, '""');
  return needsQuote ? `"${esc}"` : esc;
};

// Fields Excel loves to mangle into scientific notation / stripped leading zeros.
// Wrap non-empty values as ="…" so Excel keeps them as text.
const EXCEL_TEXT_FIELDS = new Set([
  'imei', 'serial_number', 'mac_address',
  'sim_iccid', 'sim_number',
  'beacon_uuid', 'beacon_major', 'beacon_minor',
  'beacon_namespace', 'beacon_instance_id', 'beacon_eid',
  'vin_number',
]);

const csvExcelSafeCell = (header, v) => {
  const s = String(v ?? '');
  if (s && EXCEL_TEXT_FIELDS.has(header)) {
    return `="${s.replace(/"/g, '""')}"`;
  }
  return csvEscape(s);
};

const buildTemplateCsv = (headers, exampleRow) => {
  const lines = [headers.join(',')];
  if (exampleRow) {
    lines.push(headers.map(h => csvExcelSafeCell(h, exampleRow[h] ?? '')).join(','));
  }
  return '\uFEFF' + lines.join('\n') + '\n';
};

const downloadCsv = (contents, filename) => {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadDeviceImportTemplate = (kind = 'device', withExample = false) => {
  const isBeacon = kind === 'beacon';
  const headers = isBeacon ? BEACON_TEMPLATE_HEADERS : DEVICE_TEMPLATE_HEADERS;
  const example = withExample ? (isBeacon ? BEACON_EXAMPLE_ROW : DEVICE_EXAMPLE_ROW) : null;
  const suffix = withExample ? '_example' : '_template';
  downloadCsv(buildTemplateCsv(headers, example), `${isBeacon ? 'beacons' : 'devices'}_import${suffix}.csv`);
};

/* ============================================================
 * XLSX (Excel-safe) template — pre-formats IMEI/ICCID/Mobile/etc.
 * columns as Text so Excel won't mangle them into scientific notation
 * when the user opens & saves the file.
 * ============================================================ */

// Columns that MUST stay text even after an Excel round-trip
const XLSX_TEXT_FIELDS = new Set([
  'imei', 'serial_number', 'mac_address',
  'sim_iccid', 'sim_number',
  'beacon_uuid', 'beacon_major', 'beacon_minor',
  'beacon_namespace', 'beacon_instance_id', 'beacon_eid',
  'vin_number', 'asset_year', // year alone is safe as number, but users often prepend leading zero for old records
]);

const buildTemplateXlsx = (headers, exampleRow) => {
  // Build a header row + one data row (example or blank placeholders).
  const dataRow = headers.map(h => exampleRow?.[h] ?? '');
  const rows = [headers, dataRow];
  // Additional pre-formatted blank rows so users can paste IMEIs / ICCIDs
  // into a "column that already thinks it is text".
  const BLANK_ROWS_TO_PREFORMAT = 200;
  for (let i = 0; i < BLANK_ROWS_TO_PREFORMAT; i++) {
    rows.push(headers.map(() => ''));
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Column widths
  ws['!cols'] = headers.map((h) => ({
    wch: (h.length < 14 ? 14 : Math.min(h.length + 4, 28)),
  }));

  // Force every cell in the text-columns to be Text type + text number-format.
  // This is what stops Excel from rewriting `350612073123456` as `3.50612E+14`.
  const range = XLSX.utils.decode_range(ws['!ref']);
  headers.forEach((h, colIdx) => {
    if (!XLSX_TEXT_FIELDS.has(h)) return;
    for (let r = range.s.r; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ c: colIdx, r });
      if (!ws[addr]) {
        ws[addr] = { t: 's', v: '', z: '@' };
      } else {
        ws[addr].t = 's';
        ws[addr].z = '@';
        ws[addr].v = String(ws[addr].v ?? '');
      }
    }
  });

  // Also make the header row bold-friendly (community edition can't set bold
  // colours but the frozen top row keeps the header visible when scrolling).
  ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };

  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: 'EnsueLIVETRAK Device Import Template',
    Subject: 'Device import (Excel-safe)',
    Author: 'EnsueLIVETRAK',
    CreatedDate: new Date(),
  };
  XLSX.utils.book_append_sheet(wb, ws, 'Import');
  return wb;
};

export const downloadDeviceImportTemplateXlsx = (kind = 'device', withExample = false) => {
  const isBeacon = kind === 'beacon';
  const headers = isBeacon ? BEACON_TEMPLATE_HEADERS : DEVICE_TEMPLATE_HEADERS;
  const example = withExample ? (isBeacon ? BEACON_EXAMPLE_ROW : DEVICE_EXAMPLE_ROW) : null;
  const suffix = withExample ? '_example' : '_template';
  const wb = buildTemplateXlsx(headers, example);
  XLSX.writeFile(wb, `${isBeacon ? 'beacons' : 'devices'}_import${suffix}.xlsx`);
};

/* ============================================================
 * Step chips (top-level so React can memoize it cleanly)
 * ============================================================ */
const StepChips = ({ step }) => (
  <div className="flex items-center gap-2 text-xs mb-2" data-testid="device-import-wizard-steps">
    {[1, 2, 3, 4].map(n => {
      const label = n === 1 ? 'Upload' : n === 2 ? 'Allocations' : n === 3 ? 'Review' : 'Import';
      const active = step === n;
      const done = step > n;
      return (
        <React.Fragment key={n}>
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
              active
                ? 'bg-[#2563eb] text-white border-[#2563eb]'
                : done
                  ? 'bg-green-50 text-green-800 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800'
                  : 'bg-muted text-muted-foreground border-border'
            }`}
            data-testid={`device-import-wizard-step-chip-${n}`}
          >
            <span className={`w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center ${
              active ? 'bg-white/20' : done ? 'bg-green-600 text-white' : 'bg-background text-foreground/70'
            }`}>
              {done ? <CheckCircle2 className="w-3 h-3" /> : n}
            </span>
            <span className="font-medium">{label}</span>
          </div>
          {n < 4 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
        </React.Fragment>
      );
    })}
  </div>
);

/* ============================================================
 * Sub-components — user picker (bulk), row user editor, clone source picker
 * ============================================================ */

const userLabel = (u) => u?.name || u?.full_name || u?.email || u?.id || '';

const BulkUserPicker = ({ value, onChange, users, testid }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = new Set(value || []);
  const filtered = (users || []).filter(u => {
    if (!query) return true;
    const q = query.toLowerCase();
    return String(userLabel(u)).toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q);
  }).sort((a, b) => String(userLabel(a)).localeCompare(String(userLabel(b)), undefined, { sensitivity: 'base' }));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const clear = () => onChange([]);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 justify-between min-w-[220px]" data-testid={testid}>
          <span className="truncate text-left">
            {selected.size === 0
              ? 'Pick users…'
              : selected.size === 1
                ? userLabel((users || []).find(u => u.id === Array.from(selected)[0]))
                : `${selected.size} users selected`}
          </span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[300px] z-[10000]">
        <Command>
          <CommandInput placeholder="Search users…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 60).map(u => (
                <CommandItem key={u.id} onSelect={() => toggle(u.id)} className="cursor-pointer">
                  <Checkbox checked={selected.has(u.id)} className="mr-2" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{userLabel(u)}</p>
                    {u.email && <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.size > 0 && (
          <div className="border-t p-1.5 flex justify-between">
            <span className="text-[11px] text-muted-foreground pl-1 pt-0.5">{selected.size} picked</span>
            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={clear}>Clear</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const RowUserEditor = ({ value, onChange, users, userById, testid, addLabel = '+ Add user' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = new Set(value || []);
  const filtered = (users || []).filter(u => {
    if (!query) return true;
    const q = query.toLowerCase();
    return String(userLabel(u)).toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q);
  }).sort((a, b) => String(userLabel(a)).localeCompare(String(userLabel(b)), undefined, { sensitivity: 'base' }));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const removeChip = (id) => {
    const next = Array.from(selected).filter(x => x !== id);
    onChange(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
      {(value || []).map(id => {
        const u = userById.get?.(id);
        return (
          <Badge key={id} variant="secondary" className="pl-2 pr-1 py-0.5 gap-1 text-[11px]">
            {userLabel(u) || id.slice(0, 8)}
            <button
              type="button"
              className="rounded hover:bg-foreground/10 p-0.5"
              onClick={() => removeChip(id)}
              data-testid={`${testid}-remove-${id}`}
              aria-label="Remove"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        );
      })}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs" data-testid={`${testid}-add`}>
            {addLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-[280px] z-[10000]">
          <Command>
            <CommandInput placeholder="Search users…" value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {filtered.slice(0, 60).map(u => (
                  <CommandItem key={u.id} onSelect={() => toggle(u.id)} className="cursor-pointer">
                    <Checkbox checked={selected.has(u.id)} className="mr-2" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{userLabel(u)}</p>
                      {u.email && <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const ClonePicker = ({ value, onChange, options, testid }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find(o => o.id === value);
  const filtered = (options || []).filter(d => {
    if (!query) return true;
    const q = query.toLowerCase();
    return String(d.name || '').toLowerCase().includes(q) || String(d.imei || '').toLowerCase().includes(q);
  }).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 justify-between min-w-[240px]" data-testid={testid}>
          <span className="truncate text-left">
            {selected ? selected.name : 'Pick source device…'}
          </span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[320px] z-[10000]">
        <Command>
          <CommandInput placeholder="Search devices by name or IMEI…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>No devices found.</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 80).map(d => (
                <CommandItem
                  key={d.id}
                  onSelect={() => { onChange(d.id); setOpen(false); }}
                  className="cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{d.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate font-mono">{d.imei || d.serial_number || d.id.slice(0, 8)}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/* ============================================================
 * Component
 * ============================================================ */

export default function DeviceImportWizard({
  open,
  onOpenChange,
  devices,       // existing devices (for duplicate detection AND cloning)
  users,         // [{ id, name/full_name/email }] resolvable pool for allocations
  simProviders,  // [{ id, name, ... }] for ICCID → mobile lookup
  api,
  onCompleted,
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsedHeaders, setParsedHeaders] = useState([]);
  const [ignoredHeaders, setIgnoredHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [importing, setImporting] = useState(false);
  const [failuresOpen, setFailuresOpen] = useState(false);
  // Snapshot of rows taken when entering Review — Back restores it (undoes clones/edits)
  const reviewSnapshotRef = useRef(null);
  const [results, setResults] = useState(null); // { created, skipped, failed, errors, createdDevices }
  // Step 2 (Allocations): take Allocated Users / Managed By from the file,
  // or pick custom users + management admins that REPLACE the file columns
  // on every imported row.
  const [allocSource, setAllocSource] = useState('file'); // 'file' | 'custom'
  const [customUserIds, setCustomUserIds] = useState([]);
  const [customAdminIds, setCustomAdminIds] = useState([]);
  // 'gps' | 'beacon' — auto-detected from the parsed file (mixed files are rejected)
  const [fileKind, setFileKind] = useState('gps');
  // Dry Run: on the Review step, when true, we skip the POST and just report what would
  // have been created — nothing is saved, no undo needed.
  const [dryRun, setDryRun] = useState(false);
  // Clone-from-existing-device (copies chosen fields into every selected row)
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [cloneFields, setCloneFields] = useState({
    asset_type: true,
    allocated_tenant_ids: true,
    management_admin_ids: false,
    custom_commands: true,
    io_mappings: true,
    device_icon: true,
    connected_peripherals: false,
    notes: false,
    protocol: true,
    model: true,
    meter_sources: true,
    offline_threshold: true,
    timezone_override: true,
    identity_logbook: false,
    video: false,
  });
  // Duplicate handling: 'skip' (default, never touches existing devices) or
  // 'update' (existing IMEIs/identifiers can be updated — rows default UNCHECKED).
  const [duplicateMode, setDuplicateMode] = useState('skip');
  // How updates treat existing values: 'merge' (default — only non-empty cells
  // applied, allocation lists ADDED to) or 'replace' (imported columns overwrite,
  // allocation lists become exactly the file's).
  const [fieldMode, setFieldMode] = useState('merge');
  const fileInputRef = useRef(null);

  /* Reset when opening */
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setFile(null);
    setParsedHeaders([]);
    setIgnoredHeaders([]);
    setRows([]);
    setProgress({ current: 0, total: 0 });
    setImporting(false);
    setResults(null);
    setFailuresOpen(false);
    setAllocSource('file');
    setCustomUserIds([]);
    setCustomAdminIds([]);
    setFileKind('gps');
    setDryRun(false);
    setCloneSourceId('');
    setDuplicateMode('skip');
    setFieldMode('merge');
  }, [open]);

  /* Lookup indexes */
  // The `devices` prop only holds the current paginated page (~25 rows), which
  // broke beacon cloning and cross-page duplicate detection. Fetch the full
  // fleet once per wizard open and prefer it when available.
  const [allDevices, setAllDevices] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/admin/devices?limit=100000&skip=0');
        if (!cancelled && Array.isArray(r.data)) setAllDevices(r.data);
      } catch { /* fall back to the paginated prop */ }
    })();
    return () => { cancelled = true; };
  }, [open, api]);

  const devicePool = useMemo(
    () => (allDevices.length > 0 ? allDevices : (devices || [])),
    [allDevices, devices]
  );

  const imeiToDevice = useMemo(() => {
    const m = new Map();
    devicePool.forEach(d => {
      const key = String(d.imei || '').trim().toLowerCase();
      if (key) m.set(key, d);
    });
    return m;
  }, [devicePool]);

  // Resolve users by lower-cased email, then by lower-cased name (falls back to id).
  const userByEmail = useMemo(() => {
    const m = new Map();
    (users || []).forEach(u => {
      if (u?.email) m.set(String(u.email).toLowerCase().trim(), u);
    });
    return m;
  }, [users]);

  const userByName = useMemo(() => {
    const m = new Map();
    (users || []).forEach(u => {
      const label = u?.name || u?.full_name;
      if (label) m.set(String(label).toLowerCase().trim(), u);
    });
    return m;
  }, [users]);

  const userById = useMemo(() => {
    const m = new Map();
    (users || []).forEach(u => {
      if (u?.id) m.set(u.id, u);
    });
    return m;
  }, [users]);

  // Effective allocated ids for a row (override wins over CSV-resolved).
  const effectiveUserIds = useCallback((row) => {
    if (Array.isArray(row.overrideUserIds)) return row.overrideUserIds;
    return row.resolvedUserIds || [];
  }, []);

  // Effective Management Admin ids for a row (override wins over CSV-resolved).
  const effectiveManagedIds = useCallback((row) => {
    if (Array.isArray(row.overrideManagedIds)) return row.overrideManagedIds;
    return row.resolvedManagedIds || [];
  }, []);

  // Clone sources match the file kind (beacons for beacon files, GPS otherwise)
  const cloneSourceOptions = useMemo(() => {
    return devicePool
      .filter(d => d && (fileKind === 'beacon' ? d.device_type === 'beacon' : d.device_type !== 'beacon'))
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
  }, [devicePool, fileKind]);

  // Management admins can only be Admin / Super Admin accounts
  const adminUsers = useMemo(
    () => (users || []).filter(u => u.role === 'admin' || u.role === 'super_admin'),
    [users]
  );

  // Device allocation targets: no Sub Accounts (they inherit via their End User)
  // and no chat-only contacts.
  const allocatableUsers = useMemo(
    () => (users || []).filter(u => ['super_admin', 'admin', 'manager', 'end_user'].includes(u.role)),
    [users]
  );

  // Clone field checkboxes adapt to the file kind; allocation fields are hidden
  // in custom mode because the Step-2 custom selection overrides them anyway.
  const cloneFieldOptions = useMemo(() => {
    const base = fileKind === 'beacon'
      ? [
          ['asset_type', 'Asset Type'],
          ['allocated_tenant_ids', 'Allocated Users'],
          ['management_admin_ids', 'Management List'],
          ['device_icon', 'Icon & Marker'],
          ['offline_threshold', 'Offline Threshold'],
          ['timezone_override', 'Timezone Override'],
          ['notes', 'Notes'],
        ]
      : [
          ['asset_type', 'Asset Type'],
          ['protocol', 'Protocol'],
          ['model', 'Model'],
          ['allocated_tenant_ids', 'Allocated Users'],
          ['management_admin_ids', 'Management List'],
          ['custom_commands', 'Commands'],
          ['io_mappings', 'IO Mapped'],
          ['connected_peripherals', 'Peripherals'],
          ['meter_sources', 'Speed/Odo/Engine Hrs Source'],
          ['offline_threshold', 'Offline Threshold'],
          ['timezone_override', 'Timezone Override'],
          ['identity_logbook', 'Identity & Logbooks'],
          ['video', 'Video Settings'],
          ['device_icon', 'Icon & Marker'],
          ['notes', 'Notes'],
        ];
    // Allocation cloning is never offered: with 'Use allocations from the file'
    // they come from the spreadsheet columns; with custom allocation the Step-2
    // choice applies to every row. Cloning them would silently override either.
    return base.filter(([k]) => k !== 'allocated_tenant_ids' && k !== 'management_admin_ids');
  }, [fileKind]);

  const cloneSourceDevice = useMemo(
    () => cloneSourceOptions.find(d => d.id === cloneSourceId) || null,
    [cloneSourceOptions, cloneSourceId]
  );

  /* Validation for a single row */
  const validateRow = useCallback((data, allRowsData = []) => {
    const errors = [];
    const warnings = [];

    const name = String(data.name || '').trim();
    if (!name) errors.push('Name is required');

    const deviceType = String(data.device_type || 'gps_tracker').toLowerCase().trim();
    const isBeacon = deviceType === 'beacon';

    let identifier = '';
    let existingDeviceId = null;

    if (isBeacon) {
      const btype = String(data.beacon_type || '').toLowerCase().trim();
      if (btype === 'ibeacon') {
        const uuid = String(data.beacon_uuid || '').trim();
        const major = String(data.beacon_major || '').trim();
        const minor = String(data.beacon_minor || '').trim();
        if (!uuid || !major || !minor) {
          errors.push('iBeacon requires UUID + Major + Minor');
        } else {
          identifier = `${uuid}:${major}:${minor}`.toLowerCase();
        }
      } else if (btype === 'eddystone') {
        const ns = String(data.beacon_namespace || '').trim();
        const inst = String(data.beacon_instance_id || '').trim();
        if (!ns) errors.push('Eddystone requires a Namespace');
        else identifier = (inst ? `${ns}:${inst}` : ns).toLowerCase();
      } else if (btype === 'vendor_specific') {
        const mac = String(data.mac_address || '').trim();
        const serial = String(data.serial_number || '').trim();
        if (!mac && !serial) errors.push('Vendor-specific beacon requires a MAC Address or Serial Number');
        else identifier = (mac ? normalizeMacToColon(mac) : serial).toLowerCase();
      } else {
        errors.push('Beacon requires a valid beacon_type (ibeacon, eddystone, vendor_specific)');
      }
    } else {
      // GPS-style: IMEI required and numeric
      const imei = String(data.imei || '').trim();
      const sciRe = /^-?\d+(\.\d+)?[Ee][+-]?\d+$/;
      if (!imei) errors.push('IMEI is required for GPS-style devices');
      else if (sciRe.test(imei)) errors.push(`IMEI "${imei}" is scientific-notation — Excel truncated the original 15-digit value. Re-type the correct IMEI in this cell (open the source in Google Sheets, or format the column as Text in Excel before opening).`);
      else if (!/^\d{8,20}$/.test(imei)) errors.push('IMEI must be 8-20 digits');
      identifier = imei.toLowerCase();
    }

    // Also detect Excel-corrupted ICCID (same scientific-notation pattern)
    {
      const iccid = String(data.sim_iccid || '').trim();
      const sciRe = /^-?\d+(\.\d+)?[Ee][+-]?\d+$/;
      if (iccid && sciRe.test(iccid)) {
        errors.push(`ICCID "${iccid}" is scientific-notation — Excel truncated it. Re-type the correct 19-20 digit ICCID.`);
      }
    }

    // Duplicate detection against existing devices
    if (identifier && imeiToDevice.has(identifier)) {
      existingDeviceId = imeiToDevice.get(identifier).id;
      warnings.push('An existing device already has this identifier — will be skipped');
    }

    // Duplicate detection within the same CSV
    if (identifier && errors.length === 0) {
      const dupInCsv = allRowsData.filter(d => {
        if (!d) return false;
        const otherType = String(d.device_type || 'gps_tracker').toLowerCase();
        if (otherType === 'beacon' && !isBeacon) return false;
        if (otherType !== 'beacon' && isBeacon) return false;
        if (otherType === 'beacon') {
          const btype = String(d.beacon_type || '').toLowerCase();
          if (btype === 'ibeacon') {
            const id = `${String(d.beacon_uuid || '').trim()}:${String(d.beacon_major || '').trim()}:${String(d.beacon_minor || '').trim()}`.toLowerCase();
            return id === identifier;
          }
          if (btype === 'eddystone') {
            const ns = String(d.beacon_namespace || '').trim();
            const inst = String(d.beacon_instance_id || '').trim();
            const id = (inst ? `${ns}:${inst}` : ns).toLowerCase();
            return id === identifier;
          }
          if (btype === 'vendor_specific') {
            const mac = String(d.mac_address || '').trim();
            const serial = String(d.serial_number || '').trim();
            const id = (mac ? normalizeMacToColon(mac) : serial).toLowerCase();
            return id === identifier;
          }
          return false;
        }
        return String(d.imei || '').trim().toLowerCase() === identifier;
      });
      if (dupInCsv.length > 1) {
        errors.push('Duplicate identifier within this CSV');
      }
    }

    // Protocol fallback warning
    const protoRaw = String(data.protocol || '').trim();
    if (protoRaw && !PROTOCOL_OPTIONS.some(p => p.toLowerCase() === protoRaw.toLowerCase())) {
      warnings.push(`Unknown protocol "${protoRaw}" — will default to ${isBeacon ? 'BLE Beacon' : 'Teltonika'}`);
    }

    // Asset type fallback warning
    const atRaw = String(data.asset_type || '').trim().toLowerCase();
    if (atRaw && !ASSET_TYPE_OPTIONS.includes(atRaw)) {
      warnings.push(`Unknown asset type "${data.asset_type}" — will default to "car"`);
    }

    // Resolve allocated users
    const resolvedUserIds = [];
    const missingUserRefs = [];
    const rawAlloc = String(data.allocated_tenant_ids || '').trim();
    if (rawAlloc) {
      const refs = rawAlloc.split(/[;,]/).map(s => s.trim()).filter(Boolean);
      refs.forEach(ref => {
        const lower = ref.toLowerCase();
        const u = userByEmail.get(lower) || userByName.get(lower);
        if (u) resolvedUserIds.push(u.id);
        else missingUserRefs.push(ref);
      });
      if (missingUserRefs.length > 0) {
        warnings.push(`${missingUserRefs.length} allocated user(s) not found and will be skipped: ${missingUserRefs.join(', ')}`);
      }
    }

    // Resolve "Managed By" admins (email or name; must be an Admin/Super Admin)
    const resolvedManagedIds = [];
    const missingManagedRefs = [];
    const rawManaged = String(data.management_admin_ids || '').trim();
    if (rawManaged) {
      const refs = rawManaged.split(/[;,]/).map(s => s.trim()).filter(Boolean);
      refs.forEach(ref => {
        const lower = ref.toLowerCase();
        const u = userByEmail.get(lower) || userByName.get(lower);
        if (u && (u.role === 'admin' || u.role === 'super_admin')) resolvedManagedIds.push(u.id);
        else missingManagedRefs.push(ref);
      });
      if (missingManagedRefs.length > 0) {
        warnings.push(`${missingManagedRefs.length} "Managed By" entr(ies) skipped (not found or not an Admin): ${missingManagedRefs.join(', ')}`);
      }
    }

    return {
      errors,
      warnings,
      existingDeviceId,
      isBeacon,
      identifier,
      resolvedUserIds,
      resolvedManagedIds,
      missingUserRefs,
    };
  }, [imeiToDevice, userByEmail, userByName]);

  /* Re-run validation whenever inline edits change */
  const reValidateAll = useCallback((updatedRows) => {
    const allData = updatedRows.map(r => r.data);
    return updatedRows.map(r => {
      const v = validateRow(r.data, allData);
      return { ...r, ...v };
    });
  }, [validateRow]);

  // Re-validate parsed rows once the full device pool arrives — closes a race
  // where a file uploaded before the on-open fetch resolved would miss
  // duplicate (existing IMEI/identifier) detection entirely.
  useEffect(() => {
    if (allDevices.length === 0) return;
    setRows(prev => {
      if (prev.length === 0) return prev;
      const allData = prev.map(r => r.data);
      return prev.map(r => {
        const v = validateRow(r.data, allData);
        // Newly-detected duplicates must drop back to unselected (safety default).
        const selected = v.existingDeviceId && !r.existingDeviceId ? false : r.selected;
        return { ...r, ...v, selected };
      });
    });
  }, [allDevices, validateRow]);

  /* File parsing */
  const handleFileSelected = (f) => {
    if (!f) return;
    setFile(f);
    const isXlsx = /\.xlsx$/i.test(f.name) || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    // Shared parsing pipeline — accepts an array-of-arrays [ [header...], [row1...], ... ]
    const ingest = (aoa) => {
      try {
        if (!aoa || aoa.length < 2) {
          toast.error(`${isXlsx ? 'XLSX' : 'CSV'} file has no data rows`);
          return;
        }
        const headerCells = aoa[0].map(v => String(v ?? ''));
        const normalized = headerCells.map(normalizeHeader);
        const canonical = normalized.map(h => HEADER_ALIASES[h] || null);

        const detected = [];
        const ignored = [];
        headerCells.forEach((orig, idx) => {
          const key = canonical[idx];
          if (key && IMPORT_FIELD_KEYS.has(key)) detected.push({ original: orig, key });
          else ignored.push(orig);
        });

        const parsed = [];
        for (let i = 1; i < aoa.length; i++) {
          const values = aoa[i] || [];
          // Drop rows that are entirely empty (Excel often pads a lot of blank rows below the data)
          if (values.every(v => v === undefined || v === null || String(v).trim() === '')) continue;
          const data = {};
          canonical.forEach((key, idx) => {
            if (!key) return;
            data[key] = String(values[idx] ?? '').trim();
          });
          parsed.push({ id: `row-${i}`, rowIndex: i, data, expanded: false });
        }
        if (parsed.length === 0) {
          toast.error(`${isXlsx ? 'XLSX' : 'CSV'} file has no data rows`);
          return;
        }

        const validated = reValidateAll(parsed.map(p => ({ ...p, selected: false })));

        // Reject mixed GPS + Beacon files — the split exports produce one kind
        // per file; mixed imports belong in Quick CSV Import.
        const kinds = new Set(validated.map(r => (r.isBeacon ? 'beacon' : 'gps')));
        if (kinds.size > 1) {
          toast.error(
            'Mixed GPS + Beacon file — this wizard imports one kind at a time. Use the split GPS Devices / BLE Beacons exports, or the Quick CSV Import for mixed files.',
            { duration: 12000 }
          );
          setFile(null);
          setRows([]);
          return;
        }
        setFileKind(kinds.has('beacon') ? 'beacon' : 'gps');

        // Default the Allocations step: use the file when it actually carries
        // allocation data, otherwise jump straight to custom selection.
        const hasAllocData = parsed.some(p =>
          String(p.data.allocated_tenant_ids || '').trim() || String(p.data.management_admin_ids || '').trim()
        );
        setAllocSource(hasAllocData ? 'file' : 'custom');

        const withSelected = validated.map(r => ({
          ...r,
          selected: r.errors.length === 0 && !r.existingDeviceId,
        }));

        setParsedHeaders(detected);
        setIgnoredHeaders(ignored);
        setRows(withSelected);
      } catch (err) {
        console.error('Device import parse failed:', err);
        toast.error(`Failed to parse ${isXlsx ? 'XLSX' : 'CSV'} file`);
      }
    };

    const reader = new FileReader();
    reader.onload = (ev) => {
      if (isXlsx) {
        try {
          const wb = XLSX.read(ev.target.result, { type: 'array', cellText: true, raw: false });
          const sheetName = wb.SheetNames[0];
          const ws = wb.Sheets[sheetName];
          // `header: 1` returns AoA; `raw: false` forces stringified cell values so 15-digit IMEIs survive
          const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
          ingest(aoa);
        } catch (err) {
          console.error('XLSX parse failed:', err);
          toast.error('Failed to read XLSX file');
        }
      } else {
        const text = (ev.target?.result || '').replace(/^\uFEFF/, '');
        const lines = text.split(/\r?\n/).filter((l, i) => i === 0 || l.trim().length > 0);
        const aoa = lines.map(l => parseCsvLine(l));
        ingest(aoa);
      }
    };
    if (isXlsx) reader.readAsArrayBuffer(f);
    else reader.readAsText(f);
  };

  /* Inline edit */
  const updateRowField = (rowId, field, value) => {
    setRows(prev => {
      const next = prev.map(r => r.id === rowId ? { ...r, data: { ...r.data, [field]: value } } : r);
      return reValidateAll(next);
    });
  };

  const toggleRowSelected = (rowId) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, selected: !r.selected } : r));
  };

  const toggleRowExpanded = (rowId) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, expanded: !r.expanded } : r));
  };

  const toggleSelectAll = (checked) => {
    setRows(prev => prev.map(r => ({
      ...r,
      selected: !!checked && r.errors.length === 0 && (!r.existingDeviceId || duplicateMode === 'update'),
    })));
  };

  /* Row-level allocation editor (used by expanded panel and bulk apply) */
  const setRowUserAllocation = (rowId, ids) => {
    const clean = Array.from(new Set(ids || [])).filter(Boolean);
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, overrideUserIds: clean } : r));
  };

  const setRowManagedAllocation = (rowId, ids) => {
    const clean = Array.from(new Set(ids || [])).filter(Boolean);
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, overrideManagedIds: clean } : r));
  };

  /* Bulk: clone chosen fields from an existing device onto matching rows */
  const applyCloneFrom = () => {
    if (!cloneSourceDevice) { toast.error('Choose a device to clone from'); return; }
    const src = cloneSourceDevice;
    // Only honor checkboxes that are actually visible for this file kind /
    // allocation mode — hidden fields keep stale ticked state otherwise.
    const allowed = new Set(cloneFieldOptions.map(([k]) => k));
    const on = (k) => !!cloneFields[k] && allowed.has(k);
    const keys = Object.keys(cloneFields).filter(on);
    if (keys.length === 0) { toast.error('Pick at least one field to clone'); return; }

    const patch = {};
    if (on('asset_type') && src.asset_type) patch.asset_type = src.asset_type;
    if (on('protocol') && src.protocol) patch.protocol = src.protocol;
    if (on('model') && src.model) patch.model = src.model;
    if (on('allocated_tenant_ids') && Array.isArray(src.allocated_tenant_ids)) patch.allocated_tenant_ids = src.allocated_tenant_ids.slice();
    if (on('management_admin_ids') && Array.isArray(src.management_admin_ids)) patch.management_admin_ids = src.management_admin_ids.slice();
    if (on('custom_commands') && Array.isArray(src.custom_commands)) patch.custom_commands = JSON.parse(JSON.stringify(src.custom_commands));
    if (on('io_mappings') && Array.isArray(src.io_mappings)) patch.io_mappings = JSON.parse(JSON.stringify(src.io_mappings));
    if (on('device_icon')) {
      patch.device_icon = src.device_icon || 'car';
      if (src.device_icon_color) patch.device_icon_color = src.device_icon_color;
      patch.map_marker_mode = src.map_marker_mode || 'arrow';
    }
    if (on('connected_peripherals') && Array.isArray(src.connected_peripherals)) patch.connected_peripherals = JSON.parse(JSON.stringify(src.connected_peripherals));
    if (on('notes') && src.notes) patch.notes = src.notes;
    if (on('meter_sources')) {
      patch.speed_source = src.speed_source || 'gps';
      patch.odometer_source = src.odometer_source || 'off';
      patch.engine_hours_source = src.engine_hours_source || 'off';
      patch.max_speed_kmh = Number(src.max_speed_kmh) || 200;
    }
    if (on('offline_threshold')) patch.offline_threshold_secs = Number(src.offline_threshold_secs) || 600;
    if (on('timezone_override')) {
      patch.timezone_override_enabled = !!src.timezone_override_enabled;
      patch.timezone_override = src.timezone_override || null;
    }
    if (on('identity_logbook') && src.identity_logbook && typeof src.identity_logbook === 'object') {
      patch.identity_logbook = JSON.parse(JSON.stringify(src.identity_logbook));
    }
    if (on('video')) {
      patch.has_video = !!src.has_video;
      patch.num_cameras = Number(src.num_cameras) || 0;
      patch.channel_names = { ...(src.channel_names || {}) };
      patch.always_on_streaming = !!src.always_on_streaming;
    }

    let touched = 0;
    setRows(prev => prev.map(r => {
      if (r.errors.length > 0 || r.existingDeviceId || !r.selected) return r;
      touched++;
      const newOverride = patch.allocated_tenant_ids !== undefined
        ? patch.allocated_tenant_ids
        : r.overrideUserIds;
      const newManaged = patch.management_admin_ids !== undefined
        ? patch.management_admin_ids
        : r.overrideManagedIds;
      // Asset Type: the file value always wins — clone only fills rows where it's blank,
      // written into row.data so the visible dropdown reflects it (and stays editable).
      // Protocol & Model: explicit opt-in checkboxes — cloning overwrites the row values.
      const { allocated_tenant_ids: _dropA, management_admin_ids: _dropM, asset_type: patchAsset, protocol: patchProtocol, model: patchModel, ...rest } = patch;
      let newData = (patchAsset && !r.data.asset_type)
        ? { ...r.data, asset_type: patchAsset }
        : r.data;
      if (patchProtocol || patchModel) {
        newData = {
          ...newData,
          ...(patchProtocol ? { protocol: patchProtocol } : {}),
          ...(patchModel ? { model: patchModel } : {}),
        };
      }
      return {
        ...r,
        data: newData,
        cloneOverrides: { ...(r.cloneOverrides || {}), ...rest },
        overrideUserIds: newOverride,
        overrideManagedIds: newManaged,
        cloned: true,
      };
    }));
    toast.success(`Cloned ${keys.length} field(s) from "${src.name}" onto ${touched} selected row(s)`);
  };

  /* SIM auto-lookup: mirrors Add Device — fetches provider + mobile from the SIM database by ICCID,
     falling back to the device IMEI when no ICCID is entered. */
  const lookupRowSimMobile = async (rowId) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    const iccid = String(row.data.sim_iccid || '').trim();
    const imei = String(row.data.imei || '').trim();
    if (!iccid) {
      if (imei.length < 10) {
        toast.error('Enter an ICCID (15+ digits) — or an IMEI (10+ digits) to look up by IMEI');
        return;
      }
      await lookupRowSimByImei(rowId, imei);
      return;
    }
    if (iccid.length < 15) {
      toast.error('A valid ICCID (15+ digits) is required — or clear it to look up by IMEI');
      return;
    }
    try {
      const resp = await api.post('/admin/sim-cards/lookup-by-iccids', [iccid]);
      const sim = resp.data?.results?.[iccid];
      const usedBy = resp.data?.in_use?.[iccid] || [];
      if (!sim) {
        toast.info('ICCID not found in the SIM database');
        setSimLookup(prev => ({ ...prev, [rowId]: { iccid, found: false, usedBy } }));
      } else {
        if (sim.mobile_number) updateRowField(rowId, 'sim_number', sim.mobile_number);
        if (sim.provider_name && sim.provider_name !== 'Unknown') updateRowField(rowId, 'sim_provider', sim.provider_name);
        if (sim.mobile_number) toast.success('SIM provider & mobile auto-populated');
        else toast.info('SIM found but has no mobile number on record');
        const status = String(sim.status || '').trim();
        const deactivated = !!(status && !/^(active|activated|enabled)$/i.test(status));
        if (deactivated) {
          toast.warning(`Warning: this SIM is "${status}" in the SIM database — it may not be able to report data`, { duration: 8000 });
        }
        setSimLookup(prev => ({ ...prev, [rowId]: {
          iccid,
          found: true,
          mobile: sim.mobile_number || '',
          provider: sim.provider_name || '',
          fileMobile: '', fileProvider: '',
          mobileConflict: false, providerConflict: false,
          status: sim.status || '',
          deactivated,
          usedBy,
        } }));
      }
      const othersUsing = usedBy.filter(d => d.id !== row.existingDeviceId);
      if (othersUsing.length > 0) {
        toast.warning(`Warning: this SIM is already assigned to device "${othersUsing[0].name}"${othersUsing.length > 1 ? ` and ${othersUsing.length - 1} other(s)` : ''}`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to look up SIM details');
    }
  };

  /* IMEI fallback lookup for a single row — resolves ICCID + mobile + provider from the device IMEI. */
  const lookupRowSimByImei = async (rowId, imei) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    try {
      const resp = await api.post('/admin/sim-cards/lookup-by-imeis', [imei]);
      const sim = resp.data?.results?.[imei];
      if (!sim) {
        toast.info('No SIM in the database matches this device\'s IMEI');
        setSimLookup(prev => ({ ...prev, [rowId]: { iccid: '', imei, viaImei: true, found: false, usedBy: [] } }));
        return;
      }
      if (sim.ambiguous) {
        toast.warning(`${sim.match_count} SIMs in the database match this IMEI — enter the correct ICCID manually`, { duration: 8000 });
        setSimLookup(prev => ({ ...prev, [rowId]: { iccid: '', imei, viaImei: true, found: false, ambiguous: true, matchCount: sim.match_count, usedBy: [] } }));
        return;
      }
      const usedBy = resp.data?.in_use?.[sim.iccid] || [];
      updateRowField(rowId, 'sim_iccid', sim.iccid);
      if (sim.mobile_number) updateRowField(rowId, 'sim_number', sim.mobile_number);
      if (sim.provider_name && sim.provider_name !== 'Unknown') updateRowField(rowId, 'sim_provider', sim.provider_name);
      if (sim.mobile_number) toast.success('SIM matched via IMEI — ICCID, provider & mobile auto-populated');
      else toast.info('SIM matched via IMEI but has no mobile number on record');
      const status = String(sim.status || '').trim();
      const deactivated = !!(status && !/^(active|activated|enabled)$/i.test(status));
      if (deactivated) {
        toast.warning(`Warning: this SIM is "${status}" in the SIM database — it may not be able to report data`, { duration: 8000 });
      }
      setSimLookup(prev => ({ ...prev, [rowId]: {
        iccid: sim.iccid,
        imei,
        viaImei: true,
        found: true,
        mobile: sim.mobile_number || '',
        provider: sim.provider_name || '',
        fileMobile: '', fileProvider: '',
        mobileConflict: false, providerConflict: false,
        status: sim.status || '',
        deactivated,
        usedBy,
      } }));
      const othersUsing = usedBy.filter(d => d.id !== row.existingDeviceId);
      if (othersUsing.length > 0) {
        toast.warning(`Warning: this SIM is already assigned to device "${othersUsing[0].name}"${othersUsing.length > 1 ? ` and ${othersUsing.length - 1} other(s)` : ''}`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to look up SIM by IMEI');
    }
  };

  /* Batch SIM DB lookup — fills Mobile + Provider from the SIM database (database wins).
     Rows without an ICCID fall back to an IMEI lookup (fills ICCID + Mobile + Provider). */
  const [simLookup, setSimLookup] = useState({});
  const [simFetching, setSimFetching] = useState(false);
  const fetchSimData = async () => {
    if (fileKind === 'beacon') return;
    const iccids = [...new Set(rows.filter(r => !r.isBeacon).map(r => String(r.data.sim_iccid || '').trim()).filter(i => i.length >= 15))];
    const imeis = [...new Set(rows
      .filter(r => !r.isBeacon && String(r.data.sim_iccid || '').trim().length < 15)
      .map(r => String(r.data.imei || '').trim())
      .filter(i => i.length >= 10))];
    if (iccids.length === 0 && imeis.length === 0) return;
    setSimFetching(true);
    try {
      const empty = { data: { results: {}, in_use: {} } };
      const [resp, imeiResp] = await Promise.all([
        iccids.length ? api.post('/admin/sim-cards/lookup-by-iccids', iccids) : Promise.resolve(empty),
        imeis.length ? api.post('/admin/sim-cards/lookup-by-imeis', imeis) : Promise.resolve(empty),
      ]);
      const found = resp.data?.results || {};
      const imeiFound = imeiResp.data?.results || {};
      const inUseMap = { ...(resp.data?.in_use || {}), ...(imeiResp.data?.in_use || {}) };
      const normNum = (v) => String(v || '').replace(/[\s\-()+]/g, '');
      const isActiveStatus = (s) => /^(active|activated|enabled)$/i.test(String(s || '').trim());
      const info = {};
      const iccidCounts = {};
      rows.forEach(r => {
        if (r.isBeacon) return;
        let i = String(r.data.sim_iccid || '').trim();
        if (i.length < 15) {
          const im = String(r.data.imei || '').trim();
          const sim = im.length >= 10 ? imeiFound[im] : null;
          i = sim && !sim.ambiguous ? sim.iccid : '';
        }
        if (i.length >= 15) iccidCounts[i] = (iccidCounts[i] || 0) + 1;
      });
      let fetched = 0, conflicts = 0, notFound = 0, dead = 0, dups = 0, viaImei = 0, ambiguous = 0;
      setRows(prev => prev.map(r => {
        if (r.isBeacon) return r;
        const iccid = String(r.data.sim_iccid || '').trim();
        if (iccid.length < 15) {
          /* IMEI fallback — no usable ICCID on this row */
          const imei = String(r.data.imei || '').trim();
          if (imei.length < 10) return r;
          const sim = imeiFound[imei];
          if (!sim) { info[r.id] = { iccid: '', imei, viaImei: true, found: false, usedBy: [] }; notFound++; return r; }
          if (sim.ambiguous) { info[r.id] = { iccid: '', imei, viaImei: true, found: false, ambiguous: true, matchCount: sim.match_count, usedBy: [] }; ambiguous++; return r; }
          const fileMobile = String(r.data.sim_number || '').trim();
          const fileProvider = String(r.data.sim_provider || '').trim();
          const mobileConflict = !!(fileMobile && sim.mobile_number && normNum(fileMobile) !== normNum(sim.mobile_number));
          const providerConflict = !!(fileProvider && sim.provider_name && fileProvider.toLowerCase() !== sim.provider_name.toLowerCase());
          const deactivated = !!(sim.status && !isActiveStatus(sim.status));
          const usedByOthers = (inUseMap[sim.iccid] || []).filter(d => d.id !== r.existingDeviceId);
          if ((iccidCounts[sim.iccid] || 0) > 1 || usedByOthers.length > 0) dups++;
          info[r.id] = { iccid: sim.iccid, imei, viaImei: true, found: true, mobile: sim.mobile_number, provider: sim.provider_name, fileMobile, fileProvider, mobileConflict, providerConflict, status: sim.status || '', deactivated, usedBy: inUseMap[sim.iccid] || [] };
          fetched++; viaImei++;
          if (mobileConflict || providerConflict) conflicts++;
          if (deactivated) dead++;
          return {
            ...r,
            data: {
              ...r.data,
              sim_iccid: sim.iccid,
              sim_number: sim.mobile_number || r.data.sim_number,
              sim_provider: sim.provider_name || r.data.sim_provider,
            },
          };
        }
        const usedByOthers = (inUseMap[iccid] || []).filter(d => d.id !== r.existingDeviceId);
        if ((iccidCounts[iccid] || 0) > 1 || usedByOthers.length > 0) dups++;
        const sim = found[iccid];
        if (!sim) { info[r.id] = { iccid, found: false, usedBy: inUseMap[iccid] || [] }; notFound++; return r; }
        const fileMobile = String(r.data.sim_number || '').trim();
        const fileProvider = String(r.data.sim_provider || '').trim();
        const mobileConflict = !!(fileMobile && sim.mobile_number && normNum(fileMobile) !== normNum(sim.mobile_number));
        const providerConflict = !!(fileProvider && sim.provider_name && fileProvider.toLowerCase() !== sim.provider_name.toLowerCase());
        const deactivated = !!(sim.status && !isActiveStatus(sim.status));
        info[r.id] = { iccid, found: true, mobile: sim.mobile_number, provider: sim.provider_name, fileMobile, fileProvider, mobileConflict, providerConflict, status: sim.status || '', deactivated, usedBy: inUseMap[iccid] || [] };
        fetched++;
        if (mobileConflict || providerConflict) conflicts++;
        if (deactivated) dead++;
        return {
          ...r,
          data: {
            ...r.data,
            sim_number: sim.mobile_number || r.data.sim_number,
            sim_provider: sim.provider_name || r.data.sim_provider,
          },
        };
      }));
      setSimLookup(prev => ({ ...prev, ...info }));
      const parts = [`${fetched} SIM${fetched === 1 ? '' : 's'} matched`];
      if (viaImei) parts.push(`${viaImei} matched via IMEI`);
      if (ambiguous) parts.push(`${ambiguous} ambiguous IMEI match${ambiguous === 1 ? '' : 'es'} — enter ICCID manually`);
      if (conflicts) parts.push(`${conflicts} row(s) had file values that differ — SIM database used`);
      if (dead) parts.push(`${dead} deactivated SIM(s)`);
      if (dups) parts.push(`${dups} duplicate SIM(s)`);
      if (notFound) parts.push(`${notFound} row(s) not in SIM database`);
      if (conflicts || dead || dups || ambiguous) toast.warning(`SIM lookup: ${parts.join(' · ')}`, { duration: 8000 });
      else toast.success(`SIM lookup: ${parts.join(' · ')}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'SIM database lookup failed');
    } finally {
      setSimFetching(false);
    }
  };

  // In-file duplicate ICCID map: iccid -> [row numbers]
  const iccidRowNums = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (r.isBeacon) return;
      const i = String(r.data.sim_iccid || '').trim();
      if (i.length >= 15) (m[i] = m[i] || []).push(r.rowIndex);
    });
    return m;
  }, [rows]);

  /* Counters */
  const counts = useMemo(() => {
    let ready = 0, warning = 0, error = 0, duplicates = 0;
    rows.forEach(r => {
      if (r.errors.length > 0) error++;
      else if (r.warnings.length > 0) warning++;
      else ready++;
      if (r.existingDeviceId) duplicates++;
    });
    return { ready, warning, error, duplicates, total: rows.length };
  }, [rows]);

  const selectedRows = useMemo(
    () => rows.filter(r => r.selected && r.errors.length === 0 && (!r.existingDeviceId || duplicateMode === 'update')),
    [rows, duplicateMode]
  );

  /* Build create payload from row data */
  const buildPayload = (row) => {
    const d = row.data;
    const deviceType = String(d.device_type || 'gps_tracker').toLowerCase().trim();
    const isBeacon = deviceType === 'beacon';
    const protoRaw = String(d.protocol || '').trim();
    const protocol = PROTOCOL_OPTIONS.find(p => p.toLowerCase() === protoRaw.toLowerCase())
      || (isBeacon ? 'BLE Beacon' : 'Teltonika');

    const asset_type = ASSET_TYPE_OPTIONS.includes(String(d.asset_type || '').toLowerCase())
      ? String(d.asset_type).toLowerCase()
      : 'car';

    const allocIds = effectiveUserIds(row);

    const payload = {
      name: String(d.name || '').trim(),
      device_type: deviceType,
      protocol,
      model: d.model || '',
      serial_number: d.serial_number || '',
      mac_address: d.mac_address ? normalizeMacToColon(d.mac_address) : '',
      sim_provider: d.sim_provider || '',
      sim_iccid: d.sim_iccid || '',
      sim_number: d.sim_number || '',
      asset_name: d.asset_name || '',
      asset_type,
      asset_make: d.asset_make || '',
      asset_model: d.asset_model || '',
      asset_year: d.asset_year || '',
      registration: d.registration || '',
      vin_number: d.vin_number || '',
      notes: d.notes || '',
      allocated_tenant_ids: allocIds,
    };

    const managedIds = effectiveManagedIds(row);
    if (managedIds.length > 0) {
      payload.management_admin_ids = managedIds;
    }

    if (isBeacon) {
      const btype = String(d.beacon_type || '').toLowerCase().trim();
      if (BEACON_TYPE_OPTIONS.includes(btype)) payload.beacon_type = btype;
      if (d.beacon_uuid) payload.beacon_uuid = d.beacon_uuid.trim();
      if (d.beacon_major) payload.beacon_major = d.beacon_major.trim();
      if (d.beacon_minor) payload.beacon_minor = d.beacon_minor.trim();
      if (d.beacon_namespace) payload.beacon_namespace = d.beacon_namespace.trim();
      if (d.beacon_instance_id) payload.beacon_instance_id = d.beacon_instance_id.trim();
      if (d.beacon_eid) payload.beacon_eid = d.beacon_eid.trim();
    } else {
      payload.imei = String(d.imei || '').trim();
    }

    // Merge cloned fields — structural extras (commands / IO / icons /
    // peripherals) always come from the clone; notes only fill blanks.
    if (row.cloneOverrides) {
      const co = row.cloneOverrides;
      if (co.notes && !d.notes) payload.notes = co.notes;
      if (Array.isArray(co.custom_commands)) payload.custom_commands = co.custom_commands;
      if (Array.isArray(co.io_mappings)) payload.io_mappings = co.io_mappings;
      if (Array.isArray(co.connected_peripherals)) payload.connected_peripherals = co.connected_peripherals;
      if (co.device_icon) payload.device_icon = co.device_icon;
      if (co.device_icon_color) payload.device_icon_color = co.device_icon_color;
      if (co.map_marker_mode) payload.map_marker_mode = co.map_marker_mode;
      if (co.speed_source) {
        payload.speed_source = co.speed_source;
        payload.odometer_source = co.odometer_source;
        // Personal / asset trackers never track engine hours (mirrors Add Device)
        payload.engine_hours_source = ['personal_tracker', 'asset_tracker'].includes(deviceType) ? 'off' : co.engine_hours_source;
        payload.max_speed_kmh = co.max_speed_kmh;
      }
      if (co.offline_threshold_secs) payload.offline_threshold_secs = co.offline_threshold_secs;
      if ('timezone_override_enabled' in co) {
        payload.timezone_override_enabled = co.timezone_override_enabled;
        payload.timezone_override = co.timezone_override;
      }
      if (co.identity_logbook) payload.identity_logbook = co.identity_logbook;
      if ('has_video' in co) {
        payload.has_video = co.has_video;
        payload.num_cameras = co.num_cameras;
        payload.channel_names = co.channel_names;
        payload.always_on_streaming = co.always_on_streaming;
      }
    }

    // Custom allocation (Step 2) fully overrides file columns AND clone overrides.
    if (allocSource === 'custom') {
      payload.allocated_tenant_ids = customUserIds;
      payload.management_admin_ids = customAdminIds;
    }

    return payload;
  };

  /* Build UPDATE payload for an existing device (duplicateMode === 'update').
   * merge: only non-empty cells applied; allocation lists ADDED to existing.
   * replace: every imported column overwrites (empty clears); lists become
   * exactly the file's. IMEI / identifier is the match key — never changed. */
  const buildUpdatePayload = (row) => {
    const d = row.data;
    const providedKeys = new Set(parsedHeaders.map(h => h.key));
    const existingDev = devicePool.find(x => x.id === row.existingDeviceId) || {};
    const PROFILE_KEYS = [
      'name', 'model', 'serial_number', 'mac_address',
      'sim_provider', 'sim_iccid', 'sim_number',
      'asset_name', 'asset_type', 'asset_make', 'asset_model', 'asset_year',
      'registration', 'vin_number', 'notes',
    ];
    const payload = {};
    PROFILE_KEYS.forEach(k => {
      if (!providedKeys.has(k)) return;
      let v = String(d[k] ?? '').trim();
      if (fieldMode === 'merge' && !v) return;
      if (k === 'mac_address' && v) v = normalizeMacToColon(v);
      if (k === 'asset_type') {
        const at = v.toLowerCase();
        if (!ASSET_TYPE_OPTIONS.includes(at)) return;
        v = at;
      }
      payload[k] = v;
    });
    if (providedKeys.has('protocol')) {
      const protoRaw = String(d.protocol || '').trim();
      const proto = PROTOCOL_OPTIONS.find(p => p.toLowerCase() === protoRaw.toLowerCase());
      if (proto) payload.protocol = proto;
    }
    // Managed By list (allocated users go through the dedicated allocation endpoint)
    const managedProvided = allocSource === 'custom' || providedKeys.has('management_admin_ids');
    if (managedProvided) {
      const fileManagedIds = effectiveManagedIds(row);
      payload.management_admin_ids = fieldMode === 'replace'
        ? fileManagedIds
        : Array.from(new Set([...(existingDev.management_admin_ids || []), ...fileManagedIds]));
    }
    // Allocated Users — returned separately; PUT /allocation keeps user quotas in sync.
    let allocation = null;
    const allocProvided = allocSource === 'custom' || providedKeys.has('allocated_tenant_ids');
    if (allocProvided) {
      const fileAllocIds = effectiveUserIds(row);
      const ids = fieldMode === 'replace'
        ? fileAllocIds
        : Array.from(new Set([...(existingDev.allocated_tenant_ids || []), ...fileAllocIds]));
      allocation = { allocated_user_ids: ids, confirm_remove_all: true };
    }
    return { payload, allocation };
  };

  /* Run import (or dry-run — skip network, just report what would happen) */
  const runImport = async () => {
    if (selectedRows.length === 0) {
      toast.error('No rows selected to import');
      return;
    }
    setImporting(true);
    setStep(4);
    setProgress({ current: 0, total: selectedRows.length });

    let created = 0;
    let updated = 0;
    let failed = 0;
    const errors = [];
    const createdDevices = []; // { id, name, imei }
    const preview = []; // dry-run: snapshot of what WOULD be created/updated

    for (let i = 0; i < selectedRows.length; i++) {
      const row = selectedRows[i];
      const isUpdate = !!row.existingDeviceId && duplicateMode === 'update';
      if (dryRun) {
        // Simulate — no network, no state change. Capture a payload snapshot so
        // Step 3 can show the exact record we would have written.
        const payload = buildPayload(row);
        preview.push({
          rowIndex: row.rowIndex,
          action: isUpdate ? 'update' : 'create',
          name: payload.name,
          identifier: row.isBeacon ? (row.identifier || '') : (payload.imei || ''),
          protocol: payload.protocol,
          device_type: payload.device_type,
          asset_type: payload.asset_type,
          sim_provider: payload.sim_provider,
          sim_iccid: payload.sim_iccid,
          sim_number: payload.sim_number,
          allocated_user_ids: payload.allocated_tenant_ids || [],
          managed_admin_ids: payload.management_admin_ids || [],
          registration: payload.registration,
          model: payload.model,
        });
        if (isUpdate) updated++; else created++;
      } else if (isUpdate) {
        try {
          const { payload, allocation } = buildUpdatePayload(row);
          if (Object.keys(payload).length > 0) {
            await api.put(`/admin/devices/${row.existingDeviceId}`, payload);
          }
          if (allocation) {
            await api.put(`/admin/devices/${row.existingDeviceId}/allocation`, allocation);
          }
          updated++;
        } catch (err) {
          failed++;
          const msg = err?.response?.data?.detail || err.message || 'unknown error';
          errors.push({ name: row.data.name || `Row ${row.rowIndex}`, identifier: row.identifier || '', message: `Update failed: ${String(msg)}` });
        }
      } else {
        try {
          const payload = buildPayload(row);
          const res = await api.post('/admin/devices', payload);
          if (res?.data?.id) createdDevices.push({ id: res.data.id, name: res.data.name, imei: res.data.imei });
          created++;
        } catch (err) {
          failed++;
          const msg = err?.response?.data?.detail || err.message || 'unknown error';
          errors.push({ name: row.data.name || `Row ${row.rowIndex}`, identifier: row.identifier || '', message: String(msg) });
        }
      }
      setProgress({ current: i + 1, total: selectedRows.length });
    }

    const skipped = rows.filter(r => r.existingDeviceId && !(duplicateMode === 'update' && r.selected && r.errors.length === 0)).length;

    // Dry run: pre-check device/beacon quotas so limit failures surface BEFORE
    // a real import (mirrors the backend rule: over limit when max != -1 && max > 0).
    let quotaIssues = [];
    if (dryRun) {
      const planned = new Map();
      preview.filter(p => p.action !== 'update').forEach(p => (p.allocated_user_ids || []).forEach(uid => planned.set(uid, (planned.get(uid) || 0) + 1)));
      if (planned.size > 0) {
        try {
          const r = await api.post('/admin/users/allocation-quota', { user_ids: [...planned.keys()] });
          const isBeaconKind = fileKind === 'beacon';
          (r.data || []).forEach(q => {
            const max = isBeaconKind ? q.max_beacons : q.max_devices;
            const current = isBeaconKind ? q.current_beacons : q.current_devices;
            const add = planned.get(q.user_id) || 0;
            if (max !== -1 && max > 0 && current + add > max) {
              quotaIssues.push({
                userName: q.full_name,
                current,
                max,
                planned: add,
                wouldFail: Math.min(add, (current + add) - max),
              });
            }
          });
        } catch {
          quotaIssues = [{ checkFailed: true }];
        }
      }
    }

    let logId = null;
    let importRun = null;
    if (!dryRun) {
      try {
        const r = await api.post('/admin/device-io-logs', {
          kind: 'import',
          source: 'wizard',
          file_type: /\.xlsx$/i.test(file?.name || '') ? 'xlsx' : 'csv',
          file_name: file?.name || '',
          row_count: selectedRows.length,
          created, updated, skipped, failed,
        });
        logId = r?.data?.id || null;
      } catch { /* logging must never block the import result */ }
      // Persist the run manifest so admins can Undo it (30-minute window),
      // mirroring the User Import undo model.
      if (createdDevices.length > 0) {
        try {
          const runRes = await api.post('/admin/devices/import-runs', {
            created: createdDevices.map(d => ({ device_id: d.id, imei: d.imei, name: d.name })),
            summary: { created, updated, skipped, failed, kind: fileKind },
            io_log_id: logId,
          });
          importRun = runRes?.data || null;
        } catch (recErr) {
          console.error('Failed to record import run for undo:', recErr);
        }
      }
    }
    setResults({ created, updated, skipped, failed, errors, createdDevices, dryRun, preview, logId, quotaIssues, runId: importRun?.id || null });
    setImporting(false);
    if (!dryRun && failed > 0) setFailuresOpen(true);

    const parts = [];
    if (created) parts.push(`${created} ${dryRun ? 'would be created' : 'created'}`);
    if (updated) parts.push(`${updated} ${dryRun ? 'would be updated' : 'updated'}`);
    if (skipped) parts.push(`${skipped} skipped`);
    if (failed) parts.push(`${failed} failed`);
    if (dryRun) {
      const realIssues = quotaIssues.filter(q => !q.checkFailed);
      if (realIssues.length > 0) {
        toast.warning(`Dry run found ${realIssues.reduce((s, q) => s + q.wouldFail, 0)} row(s) that WOULD FAIL due to user device/beacon limits — see details below`, { duration: 10000 });
      } else {
        toast.info(`Dry run complete — ${parts.join(', ') || 'no rows evaluated'} (nothing was saved)`);
      }
    }
    else if (failed === 0 && (created > 0 || updated > 0)) toast.success(`Import complete — ${parts.join(', ')}`);
    else if (created > 0 || updated > 0) toast.warning(`Import finished with issues — ${parts.join(', ')}`);
    else toast.error(`Import failed — ${parts.join(', ') || 'no devices imported'}`);

    if (!dryRun && typeof onCompleted === 'function') onCompleted({ created, updated, skipped, failed, run: importRun });
  };

  /* Error report download */
  const downloadErrorReport = () => {
    if (!results?.errors?.length) return;
    const rowsCsv = [
      'Device Name,Error',
      ...results.errors.map(e => `"${(e.name || '').replace(/"/g, '""')}","${(e.message || '').replace(/"/g, '""')}"`),
    ].join('\n');
    downloadCsv('\uFEFF' + rowsCsv, 'device_import_errors.csv');
  };

  /* Dry-run preview report download — exactly what would be created */
  const downloadDryRunPreview = () => {
    if (!results?.preview?.length) return;
    const isBeaconFile = fileKind === 'beacon';
    const HEADERS = [
      'Row #', 'Name', 'IMEI / Identifier', 'Protocol', 'Device Type', 'Asset Type',
      ...(isBeaconFile ? [] : ['SIM Provider', 'ICCID', 'Mobile']),
      'Registration', 'Model', 'Allocated Users', 'Managed By',
    ];
    // Fields Excel would otherwise mangle → wrap as ="..."
    const EXCEL_SAFE = new Set(['IMEI / Identifier', 'ICCID', 'Mobile']);
    const safeCell = (header, val) => {
      const s = String(val ?? '');
      if (s && EXCEL_SAFE.has(header)) return `="${s.replace(/"/g, '""')}"`;
      return csvEscape(s);
    };
    const lines = [HEADERS.join(',')];
    results.preview.forEach(p => {
      const allocLabels = (p.allocated_user_ids || [])
        .map(id => userLabel((users || []).find(u => u.id === id)) || id)
        .join('; ');
      const managedLabels = (p.managed_admin_ids || [])
        .map(id => userLabel((users || []).find(u => u.id === id)) || id)
        .join('; ');
      const cells = [
        p.rowIndex,
        p.name,
        p.identifier,
        p.protocol,
        DEVICE_TYPE_OPTIONS.find(d => d.value === p.device_type)?.label || p.device_type || '',
        labelAssetType(p.asset_type),
        ...(isBeaconFile ? [] : [p.sim_provider || '', p.sim_iccid || '', p.sim_number || '']),
        p.registration || '',
        p.model || '',
        allocLabels,
        managedLabels,
      ];
      lines.push(HEADERS.map((h, i) => safeCell(h, cells[i])).join(','));
    });
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    downloadCsv('\uFEFF' + lines.join('\n') + '\n', `device_import_dry_run_${ts}.csv`);
    toast.success('Dry-run preview downloaded');
  };

  /* Dry-run preview HTML — branded standalone HTML table (same style as History Exports) */
  const exportDryRunHtml = async () => {
    if (!results?.preview?.length) return;
    const isBeaconFile = fileKind === 'beacon';
    const HEADERS = [
      'Row #', 'Name', 'IMEI / Identifier', 'Protocol', 'Device Type', 'Asset Type',
      ...(isBeaconFile ? [] : ['SIM Provider', 'ICCID', 'Mobile']),
      'Registration', 'Model', 'Allocated Users', 'Managed By',
    ];
    const rows = results.preview.map(p => {
      const allocLabels = (p.allocated_user_ids || [])
        .map(id => userLabel((users || []).find(u => u.id === id)) || id)
        .join('; ');
      const managedLabels = (p.managed_admin_ids || [])
        .map(id => userLabel((users || []).find(u => u.id === id)) || id)
        .join('; ');
      return [
        p.rowIndex, p.name, p.identifier, p.protocol,
        DEVICE_TYPE_OPTIONS.find(d => d.value === p.device_type)?.label || p.device_type || '',
        labelAssetType(p.asset_type),
        ...(isBeaconFile ? [] : [p.sim_provider || '', p.sim_iccid || '', p.sim_number || '']),
        p.registration || '', p.model || '', allocLabels, managedLabels,
      ];
    });
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    await exportTableHtml('Device Import — Dry Run Preview',
      `${results.preview.length} row(s) — nothing was saved`,
      HEADERS, rows, `device_import_dry_run_${ts}.html`);
    toast.success('Dry-run HTML downloaded');
  };

  /* Dry-run preview PDF — Ensue-branded landscape PDF, same look as the Vehicle
     Driver Log Book / Analytics exports. Uses shared `newLandscapeDoc`,
     `addBrandHeader`, `addFooters` helpers so branding stays in lockstep. */
  const printDryRunPreview = async () => {
    if (!results?.preview?.length) { toast.error('No preview to print'); return; }
    try {
      const { doc, layout } = newLandscapeDoc();
      const { M, RIGHT, PAGE_H, CONTENT_W } = layout;
      const logo = await loadLogoPng();
      const now = new Date();
      const stampStr = now.toLocaleString();
      let y = addBrandHeader(
        doc, layout, logo,
        'Device Import — Dry Run Preview',
        `${results.preview.length} device(s) would be created  ·  ${stampStr}`,
      );

      // Amber "Dry Run" pill under the header
      doc.setFillColor(254, 243, 199);
      doc.setDrawColor(252, 211, 77);
      doc.roundedRect(M, y - 4, 62, 16, 3, 3, 'FD');
      doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(146, 64, 14);
      doc.text('DRY RUN', M + 31, y + 7, { align: 'center' });
      doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
      doc.setFontSize(9);
      doc.text('Nothing was saved. This preview is for change-management sign-off only.', M + 74, y + 7);
      y += 22;

      // Summary cards
      const cardW = (CONTENT_W - 24) / 3;
      const cards = [
        ['Would create', String(results.created), [22, 163, 74]],
        ['Skipped', String(results.skipped), SLATE_500],
        ['Failed', String(results.failed), [220, 38, 38]],
      ];
      cards.forEach(([label, val, color], i) => {
        const cx = M + i * (cardW + 12);
        doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
        doc.roundedRect(cx, y, cardW, 36, 4, 4, 'S');
        doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_500);
        doc.text(String(label).toUpperCase(), cx + 8, y + 12);
        doc.setFontSize(18); doc.setFont(undefined, 'bold'); doc.setTextColor(...color);
        doc.text(val, cx + 8, y + 30);
      });
      y += 46;

      // Table — column widths are weights, scaled so the table ALWAYS fits the
      // page (GPS used to overflow the right edge). Allocated Users / Managed By
      // wrap onto multiple lines instead of truncating names.
      const isBeaconFile = fileKind === 'beacon';
      const weightCols = [
        { label: '#',              key: 'rowIndex',    w: 22  },
        { label: 'Name',           key: 'name',        w: 95  },
        { label: 'IMEI / ID',      key: 'identifier',  w: 120, mono: true, wrap: true },
        { label: 'Protocol',       key: 'protocol',    w: 58  },
        { label: 'Device Type',    key: 'device_type', w: 62  },
        { label: 'Asset Type',     key: 'asset_type',  w: 58  },
        ...(isBeaconFile ? [] : [
          { label: 'SIM Provider', key: 'sim_provider', w: 60 },
          { label: 'ICCID',        key: 'sim_iccid',   w: 95, mono: true, wrap: true },
          { label: 'Mobile',       key: 'sim_number',  w: 68, mono: true, wrap: true },
        ]),
        { label: 'Allocated Users', key: 'alloc',   w: 100, wrap: true },
        { label: 'Managed By',      key: 'managed', w: 100, wrap: true },
      ];
      const totalW = weightCols.reduce((s, c) => s + c.w, 0);
      const scale = CONTENT_W / totalW;
      const cols = weightCols.map(c => ({ ...c, w: c.w * scale }));
      const xs = []; let cx = M;
      cols.forEach(c => { xs.push(cx); cx += c.w; });
      const LINE_H = 10;
      const ROW_PAD = 5;
      const HEAD_BG_LOCAL = [241, 245, 249];
      const ROW_BG_LOCAL = [248, 250, 252];

      const drawTableHeader = () => {
        doc.setFillColor(...HEAD_BG_LOCAL);
        doc.rect(M, y, CONTENT_W, 15, 'F');
        doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_600);
        cols.forEach((c, i) => doc.text(c.label, xs[i] + 4, y + 10));
        y += 15;
      };
      drawTableHeader();
      doc.setFont(undefined, 'normal'); doc.setFontSize(8); doc.setTextColor(...SLATE_900);

      results.preview.forEach((p, i) => {
        const alloc = (p.allocated_user_ids || [])
          .map(id => userLabel((users || []).find(u => u.id === id)) || id.slice(0, 8))
          .join(', ') || '—';
        const managed = (p.managed_admin_ids || [])
          .map(id => userLabel((users || []).find(u => u.id === id)) || id.slice(0, 8))
          .join(', ') || '—';
        const values = {
          rowIndex: String(p.rowIndex),
          name: p.name || '—',
          identifier: p.identifier || '—',
          protocol: p.protocol || '—',
          device_type: DEVICE_TYPE_OPTIONS.find(d => d.value === p.device_type)?.label || p.device_type || '—',
          asset_type: labelAssetType(p.asset_type) || '—',
          sim_provider: p.sim_provider || '—',
          sim_iccid: p.sim_iccid || '—',
          sim_number: p.sim_number || '—',
          alloc,
          managed,
        };
        // Pre-wrap each cell (wrap columns get up to 6 lines, others 1)
        const cellLines = cols.map(c => {
          if (c.mono) doc.setFont('courier', 'normal'); else doc.setFont(undefined, 'normal');
          const wrapped = doc.splitTextToSize(String(values[c.key] || ''), c.w - 8);
          return c.wrap ? wrapped.slice(0, 6) : [wrapped[0] || ''];
        });
        const maxLines = Math.max(1, ...cellLines.map(l => l.length));
        const rowH = maxLines * LINE_H + ROW_PAD;

        if (y + rowH > PAGE_H - 44) {
          doc.addPage(); y = 44;
          drawTableHeader();
          doc.setFont(undefined, 'normal'); doc.setFontSize(8); doc.setTextColor(...SLATE_900);
        }
        if (i % 2 === 1) { doc.setFillColor(...ROW_BG_LOCAL); doc.rect(M, y, CONTENT_W, rowH, 'F'); }
        cols.forEach((c, ci) => {
          if (c.mono) doc.setFont('courier', 'normal'); else doc.setFont(undefined, 'normal');
          cellLines[ci].forEach((line, li) => doc.text(line, xs[ci] + 4, y + 10 + li * LINE_H));
        });
        y += rowH;
      });
      doc.setFont(undefined, 'normal');

      // Sign-off line
      if (y + 60 > PAGE_H - 44) { doc.addPage(); y = 44; }
      y += 20;
      doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
      doc.line(M, y, M + 240, y);
      doc.line(RIGHT - 240, y, RIGHT, y);
      doc.setFontSize(8); doc.setTextColor(...SLATE_500);
      doc.text('Prepared by (signature)', M, y + 12);
      doc.text('Approved by (signature)', RIGHT, y + 12, { align: 'right' });
      doc.setTextColor(...BLUE); doc.setFontSize(8);
      doc.text('Change-management sign-off — retain with your device rollout paperwork.', M, y + 26);

      addFooters(doc, layout);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      doc.save(`device_import_dry_run_${ts}.pdf`);
      toast.success('Dry-run PDF downloaded');
    } catch (err) {
      console.error('printDryRunPreview failed', err);
      toast.error('Failed to generate PDF');
    }
  };

  /* ================================================================
   * Renderers
   * ================================================================ */

  const renderStep1 = () => (
    <div className="space-y-6" data-testid="device-import-wizard-step1">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver
            ? 'border-blue-400 bg-blue-50/60 dark:border-blue-700 dark:bg-blue-950/20'
            : file
              ? 'border-green-300 dark:border-green-800 bg-green-50/40 dark:bg-green-950/10'
              : 'border-border'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer?.files?.[0];
          if (!f) return;
          if (!/\.(csv|xlsx)$/i.test(f.name)) {
            toast.error('Only .csv and .xlsx files are supported — drop a CSV or Excel file');
            return;
          }
          handleFileSelected(f);
        }}
        data-testid="device-import-wizard-drop-zone"
      >
        {file ? (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-600 mb-3" />
            <p className="text-sm font-medium mb-1" data-testid="device-import-wizard-file-loaded">
              File loaded — {rows.length} row(s) parsed
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Review the detected columns below, then press Next.
            </p>
          </>
        ) : (
          <>
            <Download className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              Drag &amp; drop a device CSV or Excel file here, or select one (exported from EnsueLIVETRAK or matching the same column names).
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          data-testid="device-import-wizard-file-input"
          onChange={(e) => handleFileSelected(e.target.files?.[0])}
        />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          data-testid="device-import-wizard-select-file-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          {file ? 'Choose a different file' : 'Select CSV / Excel file'}
        </Button>
        {!file && (
          <>
          <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span className="font-semibold text-foreground w-24 text-right">GPS Devices:</span>
              <Button
                variant="link"
                size="sm"
                className="text-xs h-auto py-0 text-muted-foreground hover:text-foreground"
                onClick={() => downloadDeviceImportTemplate('device', true)}
                data-testid="device-import-wizard-download-device-example"
              >
                <Download className="w-3 h-3 mr-1" />
                Device Example Row (.csv)
              </Button>
              <span>·</span>
              <Button
                variant="link"
                size="sm"
                className="text-xs h-auto py-0 text-[#2563eb] hover:text-[#1d4ed8] font-medium"
                onClick={() => downloadDeviceImportTemplateXlsx('device', true)}
                data-testid="device-import-wizard-download-device-xlsx-example"
              >
                <Download className="w-3 h-3 mr-1" />
                Device Excel-Safe Example (.xlsx)
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span className="font-semibold text-foreground w-24 text-right">Beacons:</span>
              <Button
                variant="link"
                size="sm"
                className="text-xs h-auto py-0 text-muted-foreground hover:text-foreground"
                onClick={() => downloadDeviceImportTemplate('beacon', true)}
                data-testid="device-import-wizard-download-beacon-example"
              >
                <Download className="w-3 h-3 mr-1" />
                Beacon Example Row (.csv)
              </Button>
              <span>·</span>
              <Button
                variant="link"
                size="sm"
                className="text-xs h-auto py-0 text-[#2563eb] hover:text-[#1d4ed8] font-medium"
                onClick={() => downloadDeviceImportTemplateXlsx('beacon', true)}
                data-testid="device-import-wizard-download-beacon-xlsx-example"
              >
                <Download className="w-3 h-3 mr-1" />
                Beacon Excel-Safe Example (.xlsx)
              </Button>
            </div>
          </div>
          <div className="mt-3 flex items-start justify-center gap-2 text-[11px] text-amber-700 dark:text-amber-400 max-w-2xl mx-auto" data-testid="device-import-wizard-excel-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <p className="leading-snug">
              <strong>Working in Excel?</strong> Use the <strong>Excel-safe .xlsx</strong> templates &mdash; the IMEI / ICCID / Mobile columns are pre-formatted as Text so Excel won&apos;t silently truncate them into scientific notation (<code>3.50612E+14</code>) on save. Plain <code>.csv</code> is fine in Google Sheets.
            </p>
          </div>
          </>
        )}
      </div>

      {file && (
        <div className="rounded-lg border p-4 space-y-3" data-testid="device-import-wizard-file-summary">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium text-sm" data-testid="device-import-wizard-filename">{file.name}</span>
              <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
            <Badge variant="secondary" data-testid="device-import-wizard-row-count">{rows.length} row(s)</Badge>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1">Detected columns ({parsedHeaders.length})</p>
            <div className="flex flex-wrap gap-1">
              {parsedHeaders.map(h => (
                <Badge key={h.original} variant="outline" className="text-xs" data-testid={`device-import-wizard-detected-col-${h.key}`}>
                  {h.original}
                </Badge>
              ))}
            </div>
          </div>

          {ignoredHeaders.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Ignored columns ({ignoredHeaders.length})</p>
              <div className="flex flex-wrap gap-1">
                {ignoredHeaders.map(h => (
                  <Badge key={h} variant="secondary" className="text-xs opacity-70">{h}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAllocStep = () => {
    const fileUserRows = rows.filter(r => String(r.data.allocated_tenant_ids || '').trim()).length;
    const fileManagedRows = rows.filter(r => String(r.data.management_admin_ids || '').trim()).length;
    return (
      <div className="space-y-4" data-testid="device-import-wizard-step-alloc">
        <div>
          <p className="text-sm font-semibold mb-1">Where should Allocated Users &amp; Management Admins come from?</p>
          <p className="text-xs text-muted-foreground">
            Your file contains Allocated Users on <strong>{fileUserRows}</strong> of {rows.length} row(s) and Managed By on <strong>{fileManagedRows}</strong> of {rows.length} row(s).
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <button
            type="button"
            onClick={() => setAllocSource('file')}
            className={`text-left rounded-lg border p-4 transition-colors ${allocSource === 'file' ? 'border-[#2563eb] bg-blue-50/50 dark:bg-blue-950/20 ring-1 ring-[#2563eb]' : 'border-border hover:border-muted-foreground/40'}`}
            data-testid="device-import-wizard-alloc-source-file"
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-[#2563eb]" />
              <p className="text-sm font-semibold">Use allocations from the file</p>
              {allocSource === 'file' && <CheckCircle2 className="w-4 h-4 text-[#2563eb] ml-auto" />}
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Each row&apos;s <code>allocated_users</code> and <code>managed_by</code> values are resolved by email or name. Unmatched entries are skipped with a warning.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setAllocSource('custom')}
            className={`text-left rounded-lg border p-4 transition-colors ${allocSource === 'custom' ? 'border-[#2563eb] bg-blue-50/50 dark:bg-blue-950/20 ring-1 ring-[#2563eb]' : 'border-border hover:border-muted-foreground/40'}`}
            data-testid="device-import-wizard-alloc-source-custom"
          >
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-[#2563eb]" />
              <p className="text-sm font-semibold">Custom — choose here</p>
              {allocSource === 'custom' && <CheckCircle2 className="w-4 h-4 text-[#2563eb] ml-auto" />}
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Ignore the file&apos;s allocation columns. The users and management admins you pick below are applied to <strong>every</strong> imported row.
            </p>
          </button>
        </div>
        {allocSource === 'custom' && (
          <div className="rounded-lg border p-4 space-y-4 bg-background/60" data-testid="device-import-wizard-custom-alloc-panel">
            <div>
              <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-[#2563eb]" />
                Allocate imported devices to
              </p>
              <BulkUserPicker
                value={customUserIds}
                onChange={setCustomUserIds}
                users={allocatableUsers}
                testid="device-import-wizard-custom-users-picker"
              />
            </div>
            <div>
              <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-[#2563eb]" />
                Allocate Management Admins
              </p>
              <BulkUserPicker
                value={customAdminIds}
                onChange={setCustomAdminIds}
                users={adminUsers}
                testid="device-import-wizard-custom-admins-picker"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Only Admin / Super Admin accounts are listed.</p>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-400">
              These selections replace the file&apos;s Allocated Users &amp; Managed By columns on all imported rows.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderStep2 = () => (
    <div className="space-y-4 flex-1 min-h-0 flex flex-col" data-testid="device-import-wizard-step2">
      <div className="flex flex-wrap items-center gap-3">
        <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300" data-testid="device-import-wizard-count-ready">
          <CheckCircle2 className="w-3 h-3 mr-1" />Ready: {counts.ready}
        </Badge>
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" data-testid="device-import-wizard-count-warning">
          <AlertTriangle className="w-3 h-3 mr-1" />Warnings: {counts.warning}
        </Badge>
        <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300" data-testid="device-import-wizard-count-error">
          <XCircle className="w-3 h-3 mr-1" />Errors: {counts.error}
        </Badge>
        {counts.duplicates > 0 && (
          <Badge variant="secondary" data-testid="device-import-wizard-count-duplicates">
            Existing: {counts.duplicates}
          </Badge>
        )}
        <div className="ml-auto text-sm text-muted-foreground" data-testid="device-import-wizard-selected-count">
          Selected: {selectedRows.length} / {counts.total}
        </div>
      </div>

      {counts.duplicates > 0 ? (
        <div className="rounded-lg border p-3 bg-muted/40" data-testid="device-import-wizard-dup-handling">
          <Label className="text-sm font-semibold mb-2 block">Existing devices ({counts.duplicates} matched by IMEI / identifier)</Label>
          <RadioGroup value={duplicateMode} onValueChange={setDuplicateMode} className="flex gap-6">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="skip" id="dev-dup-skip" data-testid="device-import-wizard-dup-skip" />
              <Label htmlFor="dev-dup-skip" className="text-sm font-normal cursor-pointer">Skip existing devices</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="update" id="dev-dup-update" data-testid="device-import-wizard-dup-update" />
              <Label htmlFor="dev-dup-update" className="text-sm font-normal cursor-pointer">Update existing devices</Label>
            </div>
          </RadioGroup>
          {duplicateMode === 'update' && (
            <div className="mt-3 pt-3 border-t" data-testid="device-import-wizard-field-mode">
              <Label className="text-sm font-semibold mb-2 block">How should existing values be updated?</Label>
              <RadioGroup value={fieldMode} onValueChange={setFieldMode} className="flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="merge" id="dev-field-merge" className="mt-0.5" data-testid="device-import-wizard-field-merge" />
                  <Label htmlFor="dev-field-merge" className="text-sm font-normal cursor-pointer leading-snug">
                    <span className="font-medium">Merge</span>
                    <span className="block text-xs text-muted-foreground">Only cells with a value are applied — empty cells keep the device's current values. Allocated Users and Managed By are ADDED to the existing lists.</span>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="replace" id="dev-field-replace" className="mt-0.5" data-testid="device-import-wizard-field-replace" />
                  <Label htmlFor="dev-field-replace" className="text-sm font-normal cursor-pointer leading-snug">
                    <span className="font-medium">Replace</span>
                    <span className="block text-xs text-muted-foreground">Every imported column overwrites the current value (empty cells clear it). Allocated Users and Managed By become EXACTLY what the file says.</span>
                  </Label>
                </div>
              </RadioGroup>
              <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">
                Existing rows stay unticked until you select them. The IMEI / identifier is the match key and is never changed. Updates are NOT covered by the import Undo (only newly created devices are).
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border p-3 bg-muted/40 text-xs text-muted-foreground leading-snug">
          <span className="font-semibold text-foreground">Duplicate handling:</span> no rows in this file match an existing IMEI or beacon identifier — every selected row will be created as a new device.
        </div>
      )}

      {/* Clone from existing device */}
      <div className="rounded-lg border p-3 bg-background/60" data-testid="device-import-wizard-clone">
        <div className="flex items-center gap-2 mb-2">
          <Copy className="w-4 h-4 text-[#2563eb]" />
          <p className="text-sm font-semibold">Clone from an existing {fileKind === 'beacon' ? 'beacon' : 'device'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ClonePicker
            value={cloneSourceId}
            onChange={setCloneSourceId}
            options={cloneSourceOptions}
            testid="device-import-wizard-clone-picker"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={applyCloneFrom}
            disabled={!cloneSourceDevice}
            className={cloneSourceDevice ? 'bg-orange-500 hover:bg-orange-600 text-white border-orange-500 hover:text-white' : ''}
            data-testid="device-import-wizard-clone-apply"
          >
            Clone to selected
          </Button>
          <span className="text-[11px] text-muted-foreground" data-testid="device-import-wizard-clone-hint">
            Nothing is copied until you click <strong>Clone to selected</strong> — it applies the ticked fields to the selected rows below.
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {cloneFieldOptions.map(([key, label]) => (
            <label
              key={key}
              className={`flex items-center gap-1.5 text-xs ${cloneSourceId ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
              title={cloneSourceId ? undefined : 'Select a device to clone from first'}
            >
              <Checkbox
                checked={!!cloneFields[key]}
                disabled={!cloneSourceId}
                onCheckedChange={(v) => setCloneFields(prev => ({ ...prev, [key]: !!v }))}
                data-testid={`device-import-wizard-clone-field-${key}`}
              />
              {label}
            </label>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="device-import-wizard-clone-alloc-note">
          {allocSource === 'custom'
            ? 'Allocated Users & Management List cloning is hidden — your custom allocation from the previous step applies to every row.'
            : 'Allocated Users & Management List cloning is hidden — they are imported from your spreadsheet columns (Step 2: "Use allocations from the file").'}
        </p>
      </div>

      {fileKind !== 'beacon' && (
        <div className="flex flex-wrap items-center gap-2" data-testid="device-import-wizard-sim-fetch-bar">
          <Button
            size="sm"
            variant="outline"
            onClick={fetchSimData}
            disabled={simFetching}
            data-testid="device-import-wizard-fetch-sim"
          >
            <Search className="w-3.5 h-3.5 mr-1" />
            {simFetching ? 'Fetching SIM data…' : 'Fetch SIM Data'}
          </Button>
          {(() => {
            let conflicts = 0, dead = 0, dups = 0, ambiguousCount = 0;
            rows.forEach(r => {
              const iccid = String(r.data.sim_iccid || '').trim();
              const s = simLookup[r.id];
              const cur = s && s.iccid === iccid ? s : null;
              const fileDup = !r.isBeacon && iccid.length >= 15 && (iccidRowNums[iccid] || []).length > 1;
              const usedBy = (cur?.usedBy || []).filter(d => d.id !== r.existingDeviceId);
              if (fileDup || usedBy.length > 0) dups++;
              if (cur?.ambiguous) ambiguousCount++;
              if (!cur || !cur.found) return;
              if (cur.mobileConflict || cur.providerConflict) conflicts++;
              if (cur.deactivated) dead++;
            });
            return (
              <>
                {conflicts > 0 && (
                  <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400" data-testid="device-import-wizard-sim-conflict-count">
                    {conflicts} conflict{conflicts === 1 ? '' : 's'}
                  </Badge>
                )}
                {dead > 0 && (
                  <Badge variant="outline" className="text-[10px] border-red-400 text-red-600 dark:text-red-400" data-testid="device-import-wizard-sim-dead-count">
                    {dead} deactivated SIM{dead === 1 ? '' : 's'}
                  </Badge>
                )}
                {dups > 0 && (
                  <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400" data-testid="device-import-wizard-sim-dup-count">
                    {dups} duplicate SIM{dups === 1 ? '' : 's'}
                  </Badge>
                )}
                {ambiguousCount > 0 && (
                  <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400" data-testid="device-import-wizard-sim-ambiguous-count">
                    {ambiguousCount} ambiguous IMEI match{ambiguousCount === 1 ? '' : 'es'}
                  </Badge>
                )}
              </>
            );
          })()}
          <span className="text-[11px] text-muted-foreground">
            Looks up each row&apos;s ICCID in the SIM database and fills Mobile + Provider (database wins — differences from your file are flagged). Rows without an ICCID fall back to the device IMEI (fills ICCID + Mobile + Provider). Runs automatically when you arrive here.
          </span>
        </div>
      )}

      <div className="flex-1 min-h-0 border rounded-lg overflow-hidden flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={rows.length > 0 && rows.filter(r => r.errors.length === 0 && (!r.existingDeviceId || duplicateMode === 'update')).every(r => r.selected)}
                    onCheckedChange={toggleSelectAll}
                    data-testid="device-import-wizard-select-all"
                  />
                </TableHead>
                <TableHead className="w-16">Status</TableHead>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>IMEI / Identifier</TableHead>
                <TableHead>Protocol</TableHead>
                <TableHead>Device Type</TableHead>
                <TableHead>Asset Type</TableHead>
                <TableHead>Allocated</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => {
                const isError = row.errors.length > 0;
                const simInfo = simLookup[row.id];
                const simCurrent = simInfo && simInfo.iccid === String(row.data.sim_iccid || '').trim() ? simInfo : null;
                const simConflict = !!(simCurrent?.found && (simCurrent.mobileConflict || simCurrent.providerConflict));
                const simDead = !!(simCurrent?.found && simCurrent.deactivated);
                const rowIccid = String(row.data.sim_iccid || '').trim();
                const simFileDupRows = (!row.isBeacon && rowIccid.length >= 15 ? (iccidRowNums[rowIccid] || []) : []).filter(n => n !== row.rowIndex);
                const simUsedBy = (simCurrent?.usedBy || []).filter(d => d.id !== row.existingDeviceId);
                const simDup = simFileDupRows.length > 0 || simUsedBy.length > 0;
                const simAmbiguous = !!simCurrent?.ambiguous;
                const isWarn = !isError && (row.warnings.length > 0 || simConflict || simDead || simDup || simAmbiguous);
                const isDup = !!row.existingDeviceId;
                const isCloned = !!row.cloned && !isError;
                const statusIcon = isError
                  ? <XCircle className="w-4 h-4 text-red-600" data-testid={`device-import-wizard-row-status-error-${row.id}`} />
                  : isWarn
                    ? <AlertTriangle className="w-4 h-4 text-amber-600" data-testid={`device-import-wizard-row-status-warning-${row.id}`} />
                    : <CheckCircle2 className="w-4 h-4 text-green-600" data-testid={`device-import-wizard-row-status-ready-${row.id}`} />;

                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      className={isError
                        ? 'bg-red-50/50 dark:bg-red-950/20'
                        : isWarn
                          ? 'bg-amber-50/40 dark:bg-amber-950/10'
                          : isCloned
                            ? 'bg-blue-50/60 dark:bg-blue-950/20 border-l-2 border-l-[#2563eb]'
                            : ''}
                      data-cloned={isCloned ? 'true' : undefined}
                      data-testid={isCloned ? `device-import-wizard-row-cloned-${row.id}` : undefined}
                    >
                      <TableCell className="align-top pt-2">
                        <Checkbox
                          checked={row.selected}
                          disabled={isError || (isDup && duplicateMode !== 'update')}
                          onCheckedChange={() => toggleRowSelected(row.id)}
                          data-testid={`device-import-wizard-row-select-${row.id}`}
                        />
                      </TableCell>
                      <TableCell className="align-top pt-2">
                        <div className="flex flex-col items-start gap-0.5">
                          {statusIcon}
                          {isCloned && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-blue-400 text-blue-700 dark:text-blue-300">
                              Cloned
                            </Badge>
                          )}
                          {(simConflict || simDead || simDup || simAmbiguous) && (
                            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${simDead ? 'border-red-400 text-red-600 dark:text-red-400' : 'border-amber-400 text-amber-700 dark:text-amber-400'}`} data-testid={`device-import-wizard-row-sim-badge-${row.id}`}>
                              SIM
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground align-top pt-2">{row.rowIndex}</TableCell>
                      <TableCell className="align-top pt-2">
                        <Input
                          value={row.data.name || ''}
                          onChange={(e) => updateRowField(row.id, 'name', e.target.value)}
                          className="h-8 text-sm min-w-[160px]"
                          data-testid={`device-import-wizard-row-name-${row.id}`}
                        />
                      </TableCell>
                      <TableCell className="align-top pt-2">
                        {row.isBeacon ? (
                          <Input
                            readOnly
                            value={row.identifier || ''}
                            className="h-8 text-xs font-mono min-w-[220px] bg-muted/40 cursor-default"
                            title={row.identifier || ''}
                            data-testid={`device-import-wizard-row-identifier-${row.id}`}
                          />
                        ) : (
                          <Input
                            value={row.data.imei || ''}
                            onChange={(e) => updateRowField(row.id, 'imei', e.target.value)}
                            className="h-8 text-sm min-w-[160px] font-mono"
                            placeholder="IMEI"
                            data-testid={`device-import-wizard-row-imei-${row.id}`}
                          />
                        )}
                        {isDup && (
                          <Badge variant="outline" className="mt-1 text-[10px]" data-testid={`device-import-wizard-row-duplicate-${row.id}`}>
                            Existing device
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="align-top pt-2">
                        <Select
                          value={
                            PROTOCOL_OPTIONS.find(p => p.toLowerCase() === String(row.data.protocol || '').toLowerCase())
                            || (row.isBeacon ? 'BLE Beacon' : 'Teltonika')
                          }
                          onValueChange={(v) => updateRowField(row.id, 'protocol', v)}
                        >
                          <SelectTrigger className="h-8 text-sm min-w-[140px]" data-testid={`device-import-wizard-row-protocol-${row.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PROTOCOL_OPTIONS.slice().sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map(p => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top pt-2">
                        <Select
                          value={String(row.data.device_type || 'gps_tracker').toLowerCase()}
                          onValueChange={(v) => updateRowField(row.id, 'device_type', v)}
                        >
                          <SelectTrigger className="h-8 text-sm min-w-[140px]" data-testid={`device-import-wizard-row-devicetype-${row.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DEVICE_TYPE_OPTIONS
                              .filter(o => (fileKind === 'beacon' ? o.value === 'beacon' : o.value !== 'beacon'))
                              .slice()
                              .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }))
                              .map(o => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top pt-2">
                        <Select
                          value={
                            ASSET_TYPE_OPTIONS.includes(String(row.data.asset_type || '').toLowerCase())
                              ? String(row.data.asset_type).toLowerCase()
                              : undefined
                          }
                          onValueChange={(v) => updateRowField(row.id, 'asset_type', v)}
                        >
                          <SelectTrigger className="h-8 text-sm min-w-[130px]" data-testid={`device-import-wizard-row-assettype-${row.id}`}>
                            <SelectValue placeholder="— pick type —" />
                          </SelectTrigger>
                          <SelectContent>
                            {ASSET_TYPE_OPTIONS.slice().sort((a, b) => labelAssetType(a).localeCompare(labelAssetType(b), undefined, { sensitivity: 'base' })).map(t => (
                              <SelectItem key={t} value={t}>{labelAssetType(t)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs align-top pt-2">
                        {(() => {
                          if (allocSource === 'custom') {
                            return (
                              <div data-testid={`device-import-wizard-row-users-custom-${row.id}`}>
                                <div className="text-[#2563eb]">{customUserIds.length} user(s) (custom)</div>
                                <div className="text-[#2563eb]">{customAdminIds.length} admin(s) (custom)</div>
                              </div>
                            );
                          }
                          const eff = effectiveUserIds(row);
                          const managed = effectiveManagedIds(row);
                          if (eff.length === 0 && managed.length === 0 && row.missingUserRefs.length === 0) {
                            return <span className="text-muted-foreground">—</span>;
                          }
                          return (
                            <>
                              {eff.length > 0 && (
                                <div className="text-green-700 dark:text-green-400" data-testid={`device-import-wizard-row-users-ok-${row.id}`}>
                                  {eff.length} user(s)
                                </div>
                              )}
                              {managed.length > 0 && (
                                <div className="text-blue-700 dark:text-blue-400" data-testid={`device-import-wizard-row-managed-ok-${row.id}`}>
                                  {managed.length} admin(s)
                                </div>
                              )}
                              {row.missingUserRefs.length > 0 && (
                                <div className="text-amber-700 dark:text-amber-400" data-testid={`device-import-wizard-row-users-missing-${row.id}`}>
                                  {row.missingUserRefs.length} missing
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="align-top pt-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => toggleRowExpanded(row.id)}
                          data-testid={`device-import-wizard-row-expand-${row.id}`}
                        >
                          {row.expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {row.expanded && (
                      <TableRow>
                        <TableCell colSpan={10} className="bg-muted/30" data-testid={`device-import-wizard-row-details-${row.id}`}>
                          <div className="space-y-2 py-2 text-xs">
                            {row.errors.length > 0 && (
                              <div>
                                <p className="font-semibold text-red-700 dark:text-red-400 mb-1">Errors</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                  {row.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                              </div>
                            )}
                            {row.warnings.length > 0 && (
                              <div>
                                <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Warnings</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                  {row.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                              </div>
                            )}
                            {row.missingUserRefs.length > 0 && (
                              <div>
                                <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Missing users (will be skipped)</p>
                                <div className="flex flex-wrap gap-1">
                                  {row.missingUserRefs.map(n => (
                                    <Badge key={n} variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400">{n}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                              <div><span className="text-muted-foreground">Serial:</span> {row.data.serial_number || '—'}</div>
                              <div><span className="text-muted-foreground">MAC:</span> {row.data.mac_address || '—'}</div>
                              <div><span className="text-muted-foreground">Model:</span> {row.data.model || '—'}</div>
                              <div><span className="text-muted-foreground">Asset Name:</span> {row.data.asset_name || '—'}</div>
                              <div><span className="text-muted-foreground">Make/Model:</span> {(row.data.asset_make || '—') + ' / ' + (row.data.asset_model || '—')}</div>
                              <div><span className="text-muted-foreground">Year / Rego:</span> {(row.data.asset_year || '—') + ' / ' + (row.data.registration || '—')}</div>
                              <div><span className="text-muted-foreground">VIN:</span> {row.data.vin_number || '—'}</div>
                              {row.isBeacon && (
                                <>
                                  <div><span className="text-muted-foreground">Beacon Type:</span> {row.data.beacon_type || '—'}</div>
                                  {String(row.data.beacon_type || '').toLowerCase() === 'eddystone' ? (
                                    <>
                                      <div><span className="text-muted-foreground">Namespace:</span> {row.data.beacon_namespace || '—'}</div>
                                      <div data-testid={`device-import-wizard-row-instanceid-${row.id}`}><span className="text-muted-foreground">Instance ID:</span> {row.data.beacon_instance_id || '—'}</div>
                                    </>
                                  ) : String(row.data.beacon_type || '').toLowerCase() === 'eid' ? (
                                    <div><span className="text-muted-foreground">EID:</span> {row.data.beacon_eid || '—'}</div>
                                  ) : (
                                    <>
                                      <div><span className="text-muted-foreground">UUID:</span> {row.data.beacon_uuid || '—'}</div>
                                      <div><span className="text-muted-foreground">Major/Minor:</span> {(row.data.beacon_major || '—') + ' / ' + (row.data.beacon_minor || '—')}</div>
                                    </>
                                  )}
                                </>
                              )}
                            </div>

                            {/* Editable Allocated Users */}
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">Allocated Users</p>
                              {allocSource === 'custom' ? (
                                <div className="flex flex-wrap gap-1" data-testid={`device-import-wizard-row-alloc-custom-note-${row.id}`}>
                                  {customUserIds.length === 0
                                    ? <span className="text-[11px] text-muted-foreground">None — custom allocation with no users selected (Allocations step)</span>
                                    : customUserIds.map(id => (
                                      <Badge key={id} variant="secondary" className="text-[11px]">
                                        {userLabel(userById.get?.(id)) || id.slice(0, 8)}
                                      </Badge>
                                    ))}
                                </div>
                              ) : (
                                <RowUserEditor
                                  value={effectiveUserIds(row)}
                                  onChange={(ids) => setRowUserAllocation(row.id, ids)}
                                  users={allocatableUsers}
                                  userById={userById}
                                  testid={`device-import-wizard-row-alloc-${row.id}`}
                                />
                              )}
                            </div>

                            {/* Management Admins (editable in file mode; custom selection is read-only) */}
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">Management Admins</p>
                              {allocSource === 'custom' ? (
                                customAdminIds.length === 0
                                  ? <span className="text-[11px] text-muted-foreground" data-testid={`device-import-wizard-row-managed-empty-${row.id}`}>None — custom allocation with no admins selected (Allocations step)</span>
                                  : (
                                    <div className="flex flex-wrap gap-1" data-testid={`device-import-wizard-row-managed-${row.id}`}>
                                      {customAdminIds.map(id => (
                                        <Badge key={id} variant="outline" className="text-[11px] border-blue-300 text-blue-700 dark:text-blue-300">
                                          {userLabel(userById.get?.(id)) || id.slice(0, 8)}
                                        </Badge>
                                      ))}
                                      <span className="text-[10px] text-muted-foreground pt-0.5">(custom — Allocations step)</span>
                                    </div>
                                  )
                              ) : (
                                <RowUserEditor
                                  value={effectiveManagedIds(row)}
                                  onChange={(ids) => setRowManagedAllocation(row.id, ids)}
                                  users={adminUsers}
                                  userById={userById}
                                  testid={`device-import-wizard-row-managed-${row.id}`}
                                  addLabel="+ Add admin"
                                />
                              )}
                            </div>

                            {/* Editable SIM (Provider + ICCID + auto-fetch Mobile) — GPS only, beacons have no SIM */}
                            {!row.isBeacon && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">SIM (edit ICCID, then Fetch SIM — uses the device IMEI when ICCID is empty)</p>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Provider</Label>
                                  <Select
                                    value={row.data.sim_provider || ''}
                                    onValueChange={(v) => updateRowField(row.id, 'sim_provider', v === '__none__' ? '' : v)}
                                  >
                                    <SelectTrigger className="h-8 text-xs" data-testid={`device-import-wizard-row-sim-provider-${row.id}`}>
                                      <SelectValue placeholder="Select provider" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">— none —</SelectItem>
                                      {(simProviders || []).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })).map(p => (
                                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">ICCID</Label>
                                  <Input
                                    value={row.data.sim_iccid || ''}
                                    onChange={(e) => updateRowField(row.id, 'sim_iccid', e.target.value)}
                                    className="h-8 text-xs font-mono"
                                    data-testid={`device-import-wizard-row-sim-iccid-${row.id}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Mobile</Label>
                                  <Input
                                    value={row.data.sim_number || ''}
                                    onChange={(e) => updateRowField(row.id, 'sim_number', e.target.value)}
                                    className="h-8 text-xs font-mono"
                                    data-testid={`device-import-wizard-row-sim-mobile-${row.id}`}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => lookupRowSimMobile(row.id)}
                                  disabled={!row.data.sim_iccid && !String(row.data.imei || '').trim()}
                                  className="h-8"
                                  data-testid={`device-import-wizard-row-sim-fetch-${row.id}`}
                                >
                                  <Search className="w-3.5 h-3.5 mr-1" />
                                  Fetch SIM
                                </Button>
                              </div>
                              {(simCurrent || simFileDupRows.length > 0) && (
                                <div className="mt-1.5 space-y-0.5 text-[11px]">
                                  {simFileDupRows.length > 0 && (
                                    <p className="text-amber-700 dark:text-amber-400" data-testid={`device-import-wizard-row-sim-dupfile-${row.id}`}>
                                      Duplicate SIM — this ICCID is also on row{simFileDupRows.length === 1 ? '' : 's'} #{simFileDupRows.join(', #')} of this file.
                                    </p>
                                  )}
                                  {simUsedBy.length > 0 && (
                                    <p className="text-amber-700 dark:text-amber-400" data-testid={`device-import-wizard-row-sim-inuse-${row.id}`}>
                                      SIM already in use by existing device{simUsedBy.length === 1 ? '' : 's'} {simUsedBy.map(d => `"${d.name}"`).join(', ')}.
                                    </p>
                                  )}
                                  {simCurrent?.found && (
                                    <>
                                      {(simCurrent.mobileConflict || simCurrent.providerConflict) && (
                                        <div className="text-amber-700 dark:text-amber-400 space-y-0.5" data-testid={`device-import-wizard-row-sim-conflict-${row.id}`}>
                                          {simCurrent.mobileConflict && (
                                            <p>Your file listed mobile <span className="font-mono">{simCurrent.fileMobile}</span> but the SIM database says <span className="font-mono">{simCurrent.mobile}</span> — database value used.</p>
                                          )}
                                          {simCurrent.providerConflict && (
                                            <p>Your file listed provider &quot;{simCurrent.fileProvider}&quot; but the SIM database says &quot;{simCurrent.provider}&quot; — database value used.</p>
                                          )}
                                        </div>
                                      )}
                                      {simCurrent.deactivated && (
                                        <p className="text-red-600 dark:text-red-400 font-medium" data-testid={`device-import-wizard-row-sim-dead-${row.id}`}>
                                          This SIM is &quot;{simCurrent.status}&quot; in the SIM database — a dead SIM means the device may not be able to report data.
                                        </p>
                                      )}
                                      {!simCurrent.mobileConflict && !simCurrent.providerConflict && !simCurrent.deactivated && (
                                        <p className="text-green-700 dark:text-green-400" data-testid={`device-import-wizard-row-sim-fetched-${row.id}`}>
                                          {simCurrent.viaImei
                                            ? 'SIM matched by device IMEI — ICCID, mobile & provider fetched from the SIM database.'
                                            : 'Mobile & provider fetched from the SIM database.'}
                                        </p>
                                      )}
                                    </>
                                  )}
                                  {simCurrent && !simCurrent.found && (
                                    simCurrent.ambiguous ? (
                                      <p className="text-amber-700 dark:text-amber-400" data-testid={`device-import-wizard-row-sim-ambiguous-${row.id}`}>
                                        {simCurrent.matchCount} SIMs in the database match this device&apos;s IMEI — enter the correct ICCID manually, then Fetch SIM.
                                      </p>
                                    ) : (
                                      <p className="text-muted-foreground" data-testid={`device-import-wizard-row-sim-notfound-${row.id}`}>
                                        {simCurrent.viaImei
                                          ? 'No SIM in the database matches this device\'s IMEI.'
                                          : 'ICCID not found in the SIM database.'}
                                      </p>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                            )}

                            {row.cloneOverrides && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-[11px] font-semibold text-muted-foreground mb-1">Cloned fields active</p>
                                <div className="flex flex-wrap gap-1">
                                  {Object.keys(row.cloneOverrides).map(k => (
                                    <Badge key={k} variant="outline" className="text-[10px]">{k}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-3">
                              <span className="text-muted-foreground text-xs">Notes:</span> {row.data.notes || '—'}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="flex-1 min-h-0 overflow-auto pr-1" data-testid="device-import-wizard-step3">
      <div className="space-y-4">
      {importing ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm font-medium" data-testid="device-import-wizard-progress-text">
            Importing {progress.current} of {progress.total}...
          </p>
          <div className="w-full max-w-md h-2 bg-muted rounded overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      ) : results ? (
        <div className="space-y-4" data-testid="device-import-wizard-results">
          {results.dryRun && (
            <div className="rounded-lg border p-3 flex items-start gap-3 bg-amber-50/60 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800" data-testid="device-import-wizard-dryrun-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Dry run &mdash; nothing was saved</p>
                <p className="text-xs text-muted-foreground">
                  This preview shows what would be created. Close and re-open the wizard (or press Back &amp; untick &ldquo;Dry run&rdquo;) to import for real.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="device-import-wizard-dryrun-export">
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={downloadDryRunPreview} data-testid="device-import-wizard-dryrun-download">CSV</DropdownMenuItem>
                    <DropdownMenuItem onClick={exportDryRunHtml} data-testid="device-import-wizard-dryrun-html">HTML</DropdownMenuItem>
                    <DropdownMenuItem onClick={printDryRunPreview} data-testid="device-import-wizard-dryrun-print">PDF</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="default"
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                  onClick={() => { setDryRun(false); setResults(null); setStep(3); }}
                  data-testid="device-import-wizard-dryrun-confirm"
                >
                  Confirm &amp; Continue
                </Button>
              </div>
            </div>
          )}
          <div className={`grid gap-3 ${(results.updated ?? 0) > 0 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{results.dryRun ? 'Would create' : 'Created'}</p>
              <p className="text-2xl font-bold text-green-600" data-testid="device-import-wizard-result-created">{results.created}</p>
            </div>
            {(results.updated ?? 0) > 0 && (
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{results.dryRun ? 'Would update' : 'Updated'}</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="device-import-wizard-result-updated">{results.updated}</p>
              </div>
            )}
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Skipped</p>
              <p className="text-2xl font-bold text-muted-foreground" data-testid="device-import-wizard-result-skipped">{results.skipped}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold text-red-600" data-testid="device-import-wizard-result-failed">{results.failed}</p>
            </div>
          </div>

          {results.dryRun && (results.quotaIssues || []).length > 0 && (
            <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50/60 dark:bg-red-950/20 p-3" data-testid="device-import-wizard-quota-issues">
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                  {results.quotaIssues.some(q => q.checkFailed)
                    ? 'Quota check unavailable'
                    : `User ${fileKind === 'beacon' ? 'beacon' : 'device'} limits would block ${results.quotaIssues.reduce((s, q) => s + (q.wouldFail || 0), 0)} row(s)`}
                </p>
              </div>
              {results.quotaIssues.some(q => q.checkFailed) ? (
                <p className="text-xs text-red-700 dark:text-red-400">Could not verify user quotas — the real import may still fail on user limits.</p>
              ) : (
                <ul className="space-y-1 text-xs text-red-700 dark:text-red-400">
                  {results.quotaIssues.map((q, i) => (
                    <li key={i} data-testid={`device-import-wizard-quota-issue-${i}`}>
                      <strong>{q.userName}</strong> — {fileKind === 'beacon' ? 'beacon' : 'device'} limit {q.current}/{q.max} used, {q.planned} more planned → <strong>{q.wouldFail} row(s) would fail</strong>. Remove this user from those rows or raise their limit first.
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {results.dryRun && results.preview?.length > 0 && (
            <div className="rounded-lg border overflow-hidden" data-testid="device-import-wizard-dryrun-preview">
              <div className="px-3 py-2 border-b bg-muted/40 flex items-center justify-between">
                <p className="text-sm font-semibold">Preview &mdash; devices that would be created ({results.preview.length})</p>
                <Badge variant="outline" className="text-[10px]">Read-only</Badge>
              </div>
              <div className="max-h-[540px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>IMEI / Identifier</TableHead>
                      <TableHead>Protocol</TableHead>
                      <TableHead>Device Type</TableHead>
                      <TableHead>Asset Type</TableHead>
                      {fileKind !== 'beacon' && (
                        <>
                          <TableHead>SIM Provider</TableHead>
                          <TableHead>ICCID</TableHead>
                          <TableHead>Mobile</TableHead>
                        </>
                      )}
                      <TableHead>Allocated Users</TableHead>
                      <TableHead>Managed By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.preview.map((p, i) => (
                      <TableRow key={i} data-testid={`device-import-wizard-dryrun-preview-row-${i}`}>
                        <TableCell className="text-xs text-muted-foreground align-top pt-2">{p.rowIndex}</TableCell>
                        <TableCell className="align-top pt-2 text-sm font-medium">{p.name || '—'}</TableCell>
                        <TableCell className="align-top pt-2 text-xs font-mono truncate max-w-[220px]" title={p.identifier}>{p.identifier || '—'}</TableCell>
                        <TableCell className="align-top pt-2 text-xs">{p.protocol || '—'}</TableCell>
                        <TableCell className="align-top pt-2 text-xs">
                          {DEVICE_TYPE_OPTIONS.find(d => d.value === p.device_type)?.label || p.device_type || '—'}
                        </TableCell>
                        <TableCell className="align-top pt-2 text-xs">{labelAssetType(p.asset_type) || '—'}</TableCell>
                        {fileKind !== 'beacon' && (
                          <>
                            <TableCell className="align-top pt-2 text-xs">{p.sim_provider || '—'}</TableCell>
                            <TableCell className="align-top pt-2 text-xs font-mono">{p.sim_iccid || '—'}</TableCell>
                            <TableCell className="align-top pt-2 text-xs font-mono">{p.sim_number || '—'}</TableCell>
                          </>
                        )}
                        <TableCell className="align-top pt-2 text-xs">
                          {p.allocated_user_ids.length === 0
                            ? <span className="text-muted-foreground">—</span>
                            : (
                              <div className="flex flex-wrap gap-1">
                                {p.allocated_user_ids.slice(0, 3).map(id => (
                                  <Badge key={id} variant="secondary" className="text-[10px]">
                                    {userLabel((users || []).find(u => u.id === id)) || id.slice(0, 8)}
                                  </Badge>
                                ))}
                                {p.allocated_user_ids.length > 3 && (
                                  <Badge variant="outline" className="text-[10px]">+{p.allocated_user_ids.length - 3}</Badge>
                                )}
                              </div>
                            )}
                        </TableCell>
                        <TableCell className="align-top pt-2 text-xs" data-testid={`device-import-wizard-dryrun-managed-${i}`}>
                          {(p.managed_admin_ids || []).length === 0
                            ? <span className="text-muted-foreground">—</span>
                            : (
                              <div className="flex flex-wrap gap-1">
                                {p.managed_admin_ids.slice(0, 3).map(id => (
                                  <Badge key={id} variant="outline" className="text-[10px] border-blue-300 text-blue-700 dark:text-blue-300">
                                    {userLabel((users || []).find(u => u.id === id)) || id.slice(0, 8)}
                                  </Badge>
                                ))}
                                {p.managed_admin_ids.length > 3 && (
                                  <Badge variant="outline" className="text-[10px]">+{p.managed_admin_ids.length - 3}</Badge>
                                )}
                              </div>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {!results.dryRun && results.createdDevices?.length > 0 && (
            <div
              className="rounded-lg border p-3 flex items-start gap-3 bg-blue-50/60 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900"
              data-testid="device-import-wizard-undo-note"
            >
              <Undo2 className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
              <p className="text-xs text-muted-foreground">
                Changed your mind? You can undo this import for the next <span className="font-medium text-foreground">30 minutes</span> from the Undo banner on the Device Management page.
              </p>
            </div>
          )}

          {results.errors.length > 0 && (
            <div className="rounded-lg border">
              <div className="flex items-center justify-between p-3 border-b">
                <p className="text-sm font-semibold">Errors ({results.errors.length})</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setFailuresOpen(true)} data-testid="device-import-wizard-view-failures">
                    <AlertTriangle className="w-4 h-4 mr-1 text-red-600" />View failure details
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="device-import-wizard-download-errors">
                    <Download className="w-4 h-4 mr-1" />Download report
                  </Button>
                </div>
              </div>
              <ScrollArea className="max-h-[240px]">
                <div className="p-3 space-y-1 text-xs">
                  {results.errors.map((e, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="font-medium min-w-[160px] truncate">{e.name}</span>
                      <span className="text-red-600 dark:text-red-400">{e.message}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      ) : null}
      </div>
    </div>
  );

  /* Footer buttons per step */
  const canProceedFromStep1 = rows.length > 0 && counts.total > 0;
  const canImportFromStep2 = selectedRows.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && importing) return; onOpenChange(o); }}>
      <DialogContent
        className="max-w-6xl h-[85vh] flex flex-col"
        data-testid="device-import-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-[#2563eb]/10">
              <HardDrive className="w-4 h-4 text-[#2563eb]" />
            </div>
            Import Devices
          </DialogTitle>
          <DialogDescription>
            Step {step} of 4 —{' '}
            {step === 1 && 'Upload & parse CSV'}
            {step === 2 && 'Choose allocation source'}
            {step === 3 && 'Review, edit & validate rows'}
            {step === 4 && (importing ? 'Importing…' : 'Result')}
          </DialogDescription>
          <StepChips step={step} />
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {step === 1 && renderStep1()}
          {step === 2 && renderAllocStep()}
          {step === 3 && renderStep2()}
          {step === 4 && renderStep3()}
        </div>

        <DialogFooter className="gap-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="device-import-wizard-cancel-step1">
                Cancel
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!canProceedFromStep1}
                data-testid="device-import-wizard-next-step1"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="device-import-wizard-back-step-alloc">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="device-import-wizard-cancel-step-alloc">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  reviewSnapshotRef.current = JSON.parse(JSON.stringify(rows));
                  // "Use allocations from the file" → default the clone's
                  // 'Allocated Users' field to UNTICKED so cloning can't
                  // silently override the file's allocations.
                  if (allocSource === 'file') {
                    setCloneFields(prev => ({ ...prev, allocated_tenant_ids: false }));
                  }
                  setStep(3);
                  fetchSimData();
                }}
                data-testid="device-import-wizard-next-step-alloc"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  if (reviewSnapshotRef.current) {
                    setRows(reviewSnapshotRef.current);
                    setCloneSourceId('');
                    toast.info('Review changes undone — clones and row edits were reset');
                  }
                  setStep(2);
                }}
                data-testid="device-import-wizard-back-step2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <label className="flex items-center gap-2 text-xs cursor-pointer mr-auto" data-testid="device-import-wizard-dryrun-label">
                <Checkbox
                  checked={dryRun}
                  onCheckedChange={(v) => setDryRun(!!v)}
                  data-testid="device-import-wizard-dryrun"
                />
                Dry run &mdash; preview what would be created without saving
              </label>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="device-import-wizard-cancel-step2">
                Cancel
              </Button>
              <Button
                onClick={runImport}
                disabled={!canImportFromStep2}
                className={dryRun ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-[#2563eb] hover:bg-[#1d4ed8]'}
                data-testid="device-import-wizard-run-import"
              >
                {dryRun
                  ? `Run dry run (${selectedRows.length})`
                  : `Import ${selectedRows.length} device${selectedRows.length === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
          {step === 4 && (
            <>
              {results?.dryRun && !importing && (
                <Button
                  variant="outline"
                  onClick={() => { setResults(null); setStep(3); }}
                  data-testid="device-import-wizard-back-step3"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Review
                </Button>
              )}
              <Button
                onClick={() => onOpenChange(false)}
                disabled={importing}
                data-testid="device-import-wizard-close"
              >
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Failure details modal — auto-opens when a real import has failures */}
      <Dialog open={failuresOpen} onOpenChange={setFailuresOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col" data-testid="device-import-wizard-failures-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              {results?.errors?.length || 0} device(s) failed to import
            </DialogTitle>
            <DialogDescription>
              Each failed device is listed with the exact reason. Fix the cause, then re-import just those rows — already-created devices are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-0">
            <div className="space-y-2 pr-2">
              {(results?.errors || []).map((e, i) => {
                const msg = String(e.message || '');
                const low = msg.toLowerCase();
                const cat = low.includes('limit')
                  ? { label: 'User quota', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' }
                  : (low.includes('already exists') || low.includes('duplicate'))
                    ? { label: 'Duplicate', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300' }
                    : low.includes('scope')
                      ? { label: 'Permission', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300' }
                      : { label: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' };
                return (
                  <div key={i} className="rounded-lg border p-3" data-testid={`device-import-wizard-failure-${i}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge className={cat.cls}>{cat.label}</Badge>
                      <p className="text-sm font-semibold">{e.name}</p>
                      {e.identifier && <span className="text-xs font-mono text-muted-foreground">{e.identifier}</span>}
                    </div>
                    <p className="text-xs text-red-700 dark:text-red-400 leading-snug">{msg}</p>
                    {cat.label === 'User quota' && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Tip: run a <strong>Dry run</strong> first — it now pre-checks user device/beacon limits before anything is saved.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="device-import-wizard-failures-download">
              <Download className="w-4 h-4 mr-1" />Download report
            </Button>
            <Button size="sm" onClick={() => setFailuresOpen(false)} data-testid="device-import-wizard-failures-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
